import React, { useState } from 'react';
import { 
  NotebookPen, 
  ChevronDown,
  ChevronRight,
  Calendar, 
  Clock, 
  Link as LinkIcon,
  CheckSquare,
  Book,
  Users,
  Target,
  FileText,
  Lightbulb,
  Activity,
  GraduationCap,
  Download,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { getCurrentUser } from '@/data/users';

export default function LessonPlansPage() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  // Remove CS-only restriction for lesson plans - all teachers can view all strands
  const isCSOnly = false;
  
  const [expandedSession, setExpandedSession] = useState(null);
  const [selectedStrand, setSelectedStrand] = useState('strand1');

  const toggleSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  // Strand 1 Lesson Plans
  const strand1Lessons = [
    {
      id: 'unit1.1',
      unit: 'Unit 1.1',
      title: 'Future ICT Trends and Their Key Features',
      weeks: 'Weeks 1-2',
      duration: '2 Sessions (45-60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1',
          number: 1,
          title: 'Identifying and Describing Key Features',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction and Brainstorming', time: '10 min' },
            { phase: 'Focused Exploration (Group Work)', time: '20 min' },
            { phase: 'Group Sharing', time: '10 min' },
            { phase: 'Wrap-up and Transition', time: '5-10 min' }
          ],
          introduction: [
            'Revisit ICT: Begin by revisiting the concept of ICT and its current role in education, health, and home life.',
            'Brainstorm Future Technologies: Ask students to brainstorm technologies they think will be more common or impactful in these areas. Write their ideas on the board.'
          ],
          mainActivities: [
            {
              name: 'Focused Exploration (Group Work)',
              steps: [
                'Divide students into three groups: Education, Health, and Home.',
                'Assign each group to focus on identifying 2-3 potential future ICT trends relevant to their assigned area.',
                'Provide prompts if needed: AI, VR/AR, IoT, Robotics, 5G/6G, Blockchain, Biotechnology integration.',
                'Within their groups, students should: Identify 1-2 specific future ICT trends, State a key feature for each trend.',
                'Encourage them to use online resources for quick research.'
              ]
            },
            {
              name: 'Group Sharing',
              steps: [
                'Have each group share the future ICT trends they identified and one key feature for each.',
                'Facilitate a brief class discussion after each group\'s presentation.'
              ]
            },
            {
              name: 'Wrap-up and Transition',
              steps: [
                'Briefly summarize the identified trends and their initial key features.',
                'Introduce the next session, which will focus on outlining multiple key features for these trends.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Computer/tablets with internet access (for research)',
            'Handouts with guiding questions and activity instructions',
            'Flip chart paper or large sheets for group work'
          ],
          assessment: [
            'Observation of Participation: Assess student engagement in brainstorming and group discussions.',
            'Review of Brainstorming and List-Building Activities: Check the lists generated by groups for understanding.'
          ],
          homework: [
            'Research one additional future ICT trend relevant to home life and identify one key feature.',
            'Think about how the ICT trends discussed might already be present in their lives.'
          ],
          teacherNotes: [
            'Begin by making the topic engaging. Ask: "Imagine what technology will be like in 10 years"',
            'Encourage all ideas during brainstorming. Don\'t filter them at this stage.',
            'Circulate among groups to guide discussions and ensure all students are participating.',
            'Use provided examples (AI, VR/AR, IoT) to spark ideas if groups struggle.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a pre-selected list of future ICT trends. Offer sentence starters. Pair with confident learners.',
            '**For advanced learners:** Encourage them to identify more trends or delve deeper into implications. Ask them to consider the "why".'
          ]
        },
        {
          id: 'session2',
          number: 2,
          title: 'Outlining Multiple Key Features',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Review and Building Upon Previous Session', time: '10 min' },
            { phase: 'Deep Dive into Key Features (Individual/Pair Work)', time: '30 min' },
            { phase: 'Sharing and Class Discussion', time: '10-15 min' }
          ],
          introduction: [
            'Review of Previous Session: Briefly review the future ICT trends identified in the previous session for education, health, and home.',
            'Build continuity by refreshing students\' memories of what was discussed.'
          ],
          mainActivities: [
            {
              name: 'Deep Dive into Key Features (Individual/Pair Work)',
              steps: [
                'Students work individually or in pairs.',
                'Focus on one or two future ICT trends from previous session (one for each area: education, health, home).',
                'Task: Outline/list several key features for each chosen trend within its specific context.',
                'Think about: How would this technology function? What are its main components? What tasks would it enable or improve?',
                'Guiding Questions: For Education - How could it personalize learning? Automate tasks? Enhance engagement?',
                'For Health - How could it improve diagnosis? Enable remote monitoring? Personalize treatment?',
                'For Home - How could it automate tasks? Enhance security? Improve entertainment? Promote sustainability?'
              ]
            },
            {
              name: 'Sharing and Class Discussion',
              steps: [
                'Have students share key features they outlined for each trend and area.',
                'Create a class list on the board categorizing trends and features under Education, Health, and Home.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Computer/tablets with internet access',
            'Handouts with guiding questions',
            'Flip chart paper for brainstorming features',
            'Shared digital document for collaborative list building'
          ],
          assessment: [
            'Observation of Participation: Assess engagement in individual/pair work and class discussions.',
            'Review of Feature Lists: Check the lists generated to assess understanding of functionality and characteristics.',
            'Class List: The collaborative class list serves as a collective assessment of understanding.'
          ],
          homework: [
            'Research one identified ICT trend in detail and prepare a brief summary of its benefits and challenges.',
            'Identify one specific example of a future ICT trend encountered recently and list its key features.'
          ],
          teacherNotes: [
            'Make the review concise and engaging. Ask students to recall trends from memory.',
            'Emphasize that guiding questions are prompts to stimulate thinking, not a strict checklist.',
            'Encourage students to go beyond a single word for features and provide brief explanations.',
            'The class list is a powerful visual tool to consolidate learning. Ensure it is well-organized.'
          ],
          differentiation: [
            '**For struggling learners:** Provide specific examples of key features. Offer sentence starters. Allow focus on just one trend.',
            '**For advanced learners:** Identify and describe more nuanced or complex features. Consider interdependencies between features.'
          ]
        }
      ]
    },
    {
      id: 'unit1.2',
      unit: 'Unit 1.2',
      title: 'Workflows and Information Systems',
      weeks: 'Weeks 1-6',
      duration: '6 Sessions (45-60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_1.2',
          number: 1,
          title: 'Understanding Workflows',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction to Workflows', time: '10 min' },
            { phase: 'Local Examples and Parts of a Workflow', time: '15 min' },
            { phase: 'Important Aspects and the Workflow Process', time: '15 min' },
            { phase: 'Importance and Discussion', time: '10-15 min' }
          ],
          introduction: [
            'Begin by asking students about routines they follow in their daily lives (e.g., getting ready for school, preparing a meal). Relate these to the idea of a sequence of steps to achieve a goal.',
            'Introduce the term "workflow" as a more formal and organized sequence of activities within an organization.',
            'Define workflow: Guide students to collaboratively define workflow as a series of steps or tasks performed in a specific order to achieve a particular goal or outcome within an organization.'
          ],
          mainActivities: [
            {
              name: 'Local Examples and Parts of a Workflow',
              steps: [
                'Provide a pre-prepared simple example of a workflow from a local business or organization in Vanuatu (e.g., processing a customer order at a local shop, enrolling a student in a school, a basic process at a government office).',
                'Ask students to identify one specific step within the provided local example.',
                'Guide students to identify the typical stages in a workflow (e.g., Input, Process, Output; or Initiation, Execution, Completion).',
                'Discuss these stages with reference to the local example.'
              ]
            },
            {
              name: 'Important Aspects and the Workflow Process',
              steps: [
                'Lead a discussion on what makes a workflow effective (e.g., clarity, efficiency, defined roles, timelines).',
                'Brainstorm and list several important aspects on the board.',
                'As a class, collaboratively describe the general flow of a workflow from initiation to completion, emphasizing the sequential nature of tasks.',
                'Ask individual students to describe one specific aspect, such as the movement of information or a task being handed off between individuals.'
              ]
            },
            {
              name: 'Importance and Discussion',
              steps: [
                'Facilitate a discussion on why organizations use workflows. Guide students to consider benefits like: Consistency and standardization, Efficiency and reduced errors, Clarity of responsibilities, Improved communication, Better tracking and accountability.',
                'Encourage students to share their own thoughts and examples of how organized processes can be beneficial, relating it back to the local context.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Handouts with guiding questions and activity instructions',
            'Case studies or examples of local organizations (prepared beforehand)'
          ],
          assessment: [
            'Review of Definitions and Lists (e.g., collaboratively defined workflow, listed parts/aspects)',
            'Observation of Participation in discussions'
          ],
          homework: [
            'Identify a workflow in their own household or community and write down the main steps.'
          ],
          teacherNotes: [
            'Make connections to students\' daily experiences to increase engagement.',
            'Use local, culturally relevant examples from Vanuatu businesses and organizations.',
            'Encourage collaborative learning through group discussions.',
            'Write key terms and concepts on the board for visual learners.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-filled workflow templates with some steps already completed. Use visual diagrams to illustrate workflows.',
            '**For advanced learners:** Ask them to compare workflows from different types of organizations. Challenge them to identify potential improvements in sample workflows.'
          ]
        },
        {
          id: 'session2_1.2',
          number: 2,
          title: 'The Connection Between Workflows and Information Systems',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Recap of Workflows', time: '10 min' },
            { phase: 'Introducing Information Systems (IS)', time: '15 min' },
            { phase: 'Describing and Explaining the Relationship', time: '20 min' },
            { phase: 'Workflows and IS Development', time: '10-15 min' }
          ],
          introduction: [
            'Briefly review the definition and importance of workflows from the previous session.',
            'Ask students to recall examples of workflows discussed in the last class.'
          ],
          mainActivities: [
            {
              name: 'Introducing Information Systems (IS)',
              steps: [
                'Introduce the concept of an Information System as a combination of hardware, software, data, people, and processes used to collect, process, store, and distribute information.',
                'Guide students to see that IS can be used to support, automate, or manage workflows.'
              ]
            },
            {
              name: 'Describing and Explaining the Relationship',
              steps: [
                'Explain that an IS can be a tool that helps execute and manage the steps of a workflow. The workflow defines what needs to be done, and the IS provides the how.',
                'Use examples like: A customer order workflow might be managed by a sales management system. A student registration workflow might be supported by a student information system.',
                'Discuss how IS can lead to: Faster processing times, Reduced manual errors, Better data accuracy and accessibility, Improved tracking and monitoring of the workflow progress, Enhanced communication between individuals involved in the workflow.'
              ]
            },
            {
              name: 'Workflows and IS Development',
              steps: [
                'Explain that when developing a new IS, understanding the existing (or desired) workflows is crucial.',
                'IS development often involves: Analyzing existing workflows (identifying bottlenecks and areas for improvement), Designing IS to support workflows (creating features and functionalities that align with the steps of the workflow), Automating parts of workflows (using the IS to perform repetitive tasks automatically).',
                'Emphasize that a well-designed IS should streamline and enhance the efficiency and effectiveness of organizational workflows.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Computer/tablets with internet access (if available)',
            'Handouts with guiding questions and activity instructions',
            'Examples of IS supporting local workflows'
          ],
          assessment: [
            'Observation of Participation',
            'Short Answer Questions on the relationship between workflows and IS'
          ],
          homework: [
            'Research one information system used in a Vanuatu organization and describe how it supports a specific workflow.'
          ],
          teacherNotes: [
            'Use concrete, real-world examples of IS that students might be familiar with.',
            'Draw diagrams showing the relationship between workflows and IS.',
            'Emphasize that IS is a tool to improve workflows, not replace human decision-making.'
          ],
          differentiation: [
            '**For struggling learners:** Provide simplified diagrams showing workflow-IS connections. Focus on one clear example.',
            '**For advanced learners:** Ask them to analyze how a poorly designed IS might hinder a workflow. Challenge them to design a simple IS for a given workflow.'
          ]
        },
        {
          id: 'session3_1.2',
          number: 3,
          title: 'Information Systems Applications and Benefits',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction to IS Applications', time: '10 min' },
            { phase: 'Local IS Applications and Characteristics', time: '10 min' },
            { phase: 'Benefits of IS in Education and Government', time: '20 min' },
            { phase: 'Benefits in Key Economic Sectors (Vanuatu Focus)', time: '10-15 min' }
          ],
          introduction: [
            'Explain that Information Systems are implemented through various software applications designed for specific purposes.'
          ],
          mainActivities: [
            {
              name: 'Local IS Applications and Characteristics',
              steps: [
                'Provide or ask students to identify a common IS application used in Vanuatu (e.g., point-of-sale systems in shops, basic accounting software, government websites for information).',
                'Discuss general characteristics like user-friendliness, data storage, processing capabilities, security.',
                'Brainstorm and list several key characteristics.',
                'Ask students to explain one characteristic in more detail.'
              ]
            },
            {
              name: 'Benefits of IS in Education and Government',
              steps: [
                'Discuss benefits in Education like improved access to information, personalized learning, efficient administration.',
                'Brainstorm and list several benefits (e.g., online resources, learning management systems, digital record-keeping).',
                'Ask students to elaborate on one benefit with examples.',
                'Discuss benefits in Government like improved service delivery, efficient data management, enhanced transparency.',
                'Brainstorm and list several benefits (e.g., online portals, citizen databases, digital communication).',
                'Ask students to elaborate on one benefit with examples relevant to Vanuatu.'
              ]
            },
            {
              name: 'Benefits in Key Economic Sectors (Vanuatu Focus)',
              steps: [
                'Discuss specific sectors relevant to Vanuatu (e.g., Tourism, Agriculture, Fisheries) and how IS can benefit them.',
                'Tourism: Online booking systems, marketing platforms, customer relationship management.',
                'Agriculture: Information on weather patterns, market prices, efficient supply chain management.',
                'Fisheries: Tracking fishing activities, managing resources, connecting with markets.',
                'Encourage students to think critically about how IS can contribute to economic growth, efficiency, and sustainability in these sectors within the Vanuatu context.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Computer/tablets with internet access (if available)',
            'Examples of IS applications used in Vanuatu (screenshots, descriptions)'
          ],
          assessment: [
            'Naming local IS applications',
            'Listing and describing IS characteristics',
            'Explaining benefits of IS in different sectors',
            'Case Study Analysis (focusing on IS benefits)'
          ],
          homework: [
            'Interview a family member or community member who uses an IS in their work and report on its benefits and challenges.'
          ],
          teacherNotes: [
            'Focus on Vanuatu-specific examples to maintain relevance.',
            'Use visual aids like screenshots of local IS applications.',
            'Connect IS benefits to students\' everyday experiences.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a list of IS benefits to choose from. Use matching activities for characteristics.',
            '**For advanced learners:** Ask them to evaluate the effectiveness of a local IS application. Challenge them to propose improvements.'
          ]
        },
        {
          id: 'session4_1.2',
          number: 4,
          title: 'Information Systems Development',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction to IS Development', time: '10 min' },
            { phase: 'Development Methods', time: '15 min' },
            { phase: 'The SDLC Process', time: '25 min' },
            { phase: 'Main Features of SDLC Stages', time: '10-15 min' }
          ],
          introduction: [
            'Define Information Systems development: Explain it as the process of creating, modifying, and maintaining information systems.'
          ],
          mainActivities: [
            {
              name: 'Development Methods',
              steps: [
                'Introduce a common method like the Systems Development Life Cycle (SDLC) or Agile methodologies.',
                'Briefly mention other methods (e.g., prototyping, end-user development).',
                'Explain the chosen method (e.g., SDLC) with its typical phases.'
              ]
            },
            {
              name: 'The SDLC Process',
              steps: [
                'Identify one stage of the SDLC (e.g., analysis, design).',
                'Detail the typical phases of the SDLC: Planning, Analysis, Design, Implementation, Testing, Deployment, Maintenance.',
                'Describe what happens in each stage of the SDLC.',
                'Encourage students to think about the iterative nature and the importance of user involvement.'
              ]
            },
            {
              name: 'Main Features of SDLC Stages',
              steps: [
                'System planning: Identify goal setting and feasibility studies.',
                'System analysis: Focus on understanding user needs and system requirements.',
                'System design: Emphasize creating blueprints for the system.',
                'System implementation: Highlight the coding and building of the system.',
                'System operation management: Focus on the day-to-day running and maintenance.',
                'System evaluation: Emphasize assessing the system\'s effectiveness and making improvements.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Handouts explaining SDLC phases',
            'Diagrams of IS development processes'
          ],
          assessment: [
            'Defining IS development',
            'Listing and describing SDLC phases',
            'Explaining the main feature of each SDLC stage',
            'Short Answer Questions on IS development processes'
          ],
          homework: [
            'Create a simple diagram illustrating the SDLC process for a hypothetical school information system.'
          ],
          teacherNotes: [
            'Use a visual diagram of the SDLC throughout the lesson.',
            'Relate each SDLC phase to something students can understand (like planning a school event).',
            'Emphasize that development is not a one-time event but a continuous cycle.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-labeled SDLC diagrams to complete. Use simple analogies for each phase.',
            '**For advanced learners:** Compare SDLC with Agile methodologies. Ask them to critique the SDLC approach.'
          ]
        },
        {
          id: 'session5_1.2',
          number: 5,
          title: 'Managing Information Systems in Vanuatu',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction to IS Management', time: '10 min' },
            { phase: 'Key Management Requirements', time: '20 min' },
            { phase: 'Discussion on Management Requirements', time: '15-20 min' }
          ],
          introduction: [
            'Explain that once an IS is developed and implemented, it needs to be effectively managed to ensure it continues to meet organizational needs.'
          ],
          mainActivities: [
            {
              name: 'Key Management Requirements',
              steps: [
                'Discuss requirements like technical support, user training, data security, regular maintenance, software updates.',
                'Brainstorm and list several key requirements.',
                'Ask students to explain one requirement in detail (e.g., the importance of data backups for preventing data loss).',
                'For a chosen requirement (e.g., user training), discuss how proper training leads to more efficient use, fewer errors, and greater user satisfaction.'
              ]
            },
            {
              name: 'Discussion on Management Requirements',
              steps: [
                'Facilitate a discussion on the challenges and considerations for managing IS in the context of Vanuatu (e.g., limited technical expertise, internet connectivity issues, cost of maintenance).',
                'Encourage students to think about prioritizing these requirements.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Case studies on IS management challenges in developing countries',
            'Examples of IS management tools'
          ],
          assessment: [
            'Listing and describing IS management requirements',
            'Explaining the impact of good management practices',
            'Observation of participation in discussion',
            'Problem-Solving Scenarios related to IS management in Vanuatu'
          ],
          homework: [
            'Write a one-page reflection on the biggest challenges to IS management in Vanuatu and propose one practical solution.'
          ],
          teacherNotes: [
            'Be sensitive to the challenges faced by Vanuatu in the technology sector.',
            'Encourage practical, locally-relevant solutions.',
            'Use real-world examples from Vanuatu organizations where possible.'
          ],
          differentiation: [
            '**For struggling learners:** Provide guided questions for the reflection. Offer a framework for problem-solving.',
            '**For advanced learners:** Ask them to research best practices from other Pacific Island nations. Challenge them to develop a management plan for a hypothetical IS.'
          ]
        },
        {
          id: 'session6_1.2',
          number: 6,
          title: 'Implementing Information Systems in Vanuatu',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction to IS Implementation Challenges', time: '10 min' },
            { phase: 'Identifying and Describing Implementation Issues', time: '20 min' },
            { phase: 'Explaining and Discussing Main Issues', time: '15-20 min' }
          ],
          introduction: [
            'Explain that implementing new IS can be challenging, especially in developing countries like Vanuatu and other Pacific Island Countries (PICs).'
          ],
          mainActivities: [
            {
              name: 'Identifying and Describing Implementation Issues',
              steps: [
                'Discuss common issues like limited infrastructure (internet, electricity), lack of skilled personnel, cultural barriers to technology adoption, funding constraints, dependence on external expertise.',
                'Brainstorm and list several issues.',
                'Ask students to describe one issue in detail, explaining its impact.'
              ]
            },
            {
              name: 'Explaining and Discussing Main Issues',
              steps: [
                'Elaborate on the key challenges, providing context specific to Vanuatu where possible (e.g., geographical remoteness impacting connectivity, reliance on aid for funding).',
                'Encourage students to think critically about potential solutions and strategies to overcome these challenges, considering local resources and cultural contexts.'
              ]
            }
          ],
          materials: [
            'Whiteboard or projector',
            'Markers or pens',
            'Articles/reports on IS implementation challenges in Pacific Island Countries',
            'Maps of Vanuatu to illustrate geographical challenges'
          ],
          assessment: [
            'Listing and describing IS implementation issues in Vanuatu',
            'Explaining the impact of these issues',
            'Problem-Solving Scenarios related to IS implementation challenges',
            'Presentations (optional, on specific implementation challenges)'
          ],
          homework: [
            'Prepare a short presentation on one major IS implementation challenge in Vanuatu and propose realistic solutions.'
          ],
          teacherNotes: [
            'Frame challenges as opportunities for innovation and local solutions.',
            'Highlight success stories of IS implementation in Vanuatu or similar contexts.',
            'Encourage students to think about their role in Vanuatu\'s digital future.'
          ],
          differentiation: [
            '**For struggling learners:** Provide templates for presentations. Offer a list of pre-researched challenges to choose from.',
            '**For advanced learners:** Ask them to compare Vanuatu\'s challenges with another developing nation. Challenge them to propose a comprehensive implementation strategy.'
          ]
        }
      ]
    },
    {
      id: 'unit1.3',
      unit: 'Unit 1.3',
      title: 'ICT Fundamentals and Software Choices',
      weeks: 'Weeks 1-5',
      duration: '5 Sessions (45-60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_1.3',
          number: 1,
          title: 'ICT Infrastructure Requirements',
          duration: '45 minutes',
          structure: [
            { phase: 'Introduction & Brainstorming', time: '10 min' },
            { phase: 'Identifying Basic Requirements', time: '15 min' },
            { phase: 'Sketching Key Components', time: '15 min' },
            { phase: 'Group Presentations', time: '5 min' }
          ],
          introduction: [
            'Begin with a class discussion on what students think an organization needs in terms of technology to function effectively (e.g., computers, internet). Introduce the term "ICT Infrastructure."'
          ],
          mainActivities: [
            {
              name: 'Identifying Basic Requirements',
              steps: [
                'Guide students to identify and list basic ICT infrastructure requirements for a small organization (e.g., computers, network devices (router, switch), internet connection, server (optional), printers, software).'
              ]
            },
            {
              name: 'Sketching Key Components',
              steps: [
                'Divide students into small groups. Assign each group a key component of ICT infrastructure (e.g., computer, router, server).',
                'Ask them to create a simple sketch of their assigned component and label its basic parts.'
              ]
            },
            {
              name: 'Group Presentations',
              steps: [
                'Each group briefly presents their sketch and explains the function of the component.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Markers/Pens',
            'Paper for sketching'
          ],
          assessment: [
            'Observation of group discussions and sketches'
          ],
          teacherNotes: [
            'Adapt the complexity of the "organization" scenario to the students\' prior knowledge.',
            'Ensure clear definitions of key infrastructure terms.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-drawn outlines for sketches or a list of components to choose from. Focus on identifying only 2-3 essential components.',
            '**For advanced learners:** Ask them to consider more advanced infrastructure like firewalls or cloud storage. Encourage them to think about the interdependencies between components.'
          ]
        },
        {
          id: 'session2_1.3',
          number: 2,
          title: 'ICT Infrastructure Planning and Issues',
          duration: '45 minutes',
          structure: [
            { phase: 'Case Study: Infrastructure Planning', time: '15 min' },
            { phase: 'Introduction to ICT Infrastructure Reports', time: '15 min' },
            { phase: 'Identifying Implementation Issues', time: '10 min' },
            { phase: 'Class Discussion', time: '5 min' }
          ],
          introduction: [
            'Present a simple scenario of a local organization (e.g., a small retail shop) and its basic ICT needs.'
          ],
          mainActivities: [
            {
              name: 'Infrastructure Planning',
              steps: [
                'Guide students to collaboratively create a basic plan outlining the necessary infrastructure components and their potential arrangement.'
              ]
            },
            {
              name: 'ICT Infrastructure Reports',
              steps: [
                'Explain the purpose of an ICT infrastructure report (documenting needs, costs, and plans).',
                'Briefly discuss the key sections of a simple report (introduction, requirements, proposed infrastructure, budget considerations).'
              ]
            },
            {
              name: 'Implementation Issues',
              steps: [
                'Brainstorm potential issues that could arise when implementing an ICT infrastructure (e.g., cost, lack of expertise, power outages, internet reliability, security concerns).',
                'Discuss one or two identified issues in more detail, exploring their potential impact.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Case study handouts'
          ],
          assessment: [
            'Participation in planning activity and brainstorming',
            'Review of basic report outlines (if assigned as homework)'
          ],
          homework: [
            'Students to outline the key sections of an ICT infrastructure report for the case study organization'
          ],
          teacherNotes: [
            'Emphasize the importance of a structured approach to planning.',
            'Relate implementation issues to the local context of Vanuatu.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a template for the infrastructure plan and report outline. Focus on identifying only 2-3 common implementation issues.',
            '**For advanced learners:** Challenge them to consider more complex planning aspects like scalability, disaster recovery, and different vendor options. Ask them to propose solutions for the identified issues.'
          ]
        },
        {
          id: 'session3_1.3',
          number: 3,
          title: 'Concerns and Networking Fundamentals',
          duration: '45 minutes',
          structure: [
            { phase: 'Deep Dive into Implementation Concerns', time: '15 min' },
            { phase: 'Introduction to Network Types', time: '15 min' },
            { phase: 'Quick Quiz', time: '10 min' },
            { phase: 'Preview', time: '5 min' }
          ],
          introduction: [
            'Expand on the issues discussed in the previous session.',
            'Focus on concerns relevant to a local organization in Vanuatu (e.g., limited internet bandwidth, availability of skilled technicians, cost of imported equipment, environmental factors).'
          ],
          mainActivities: [
            {
              name: 'Implementation Concerns',
              steps: [
                'Facilitate a discussion on how these concerns might impact infrastructure choices.',
                'Consider local Vanuatu context throughout.'
              ]
            },
            {
              name: 'Network Types',
              steps: [
                'Introduce the four main types of networks (LAN, WAN, MAN, Wi-fi).',
                'For each type, clearly state and explain a key distinguishing feature (e.g., LAN: limited geographical area; WAN: covers a large geographical area; MAN: covers a city; Wi-fi: wireless connectivity).',
                'Use simple diagrams to illustrate the scope of each network type.'
              ]
            },
            {
              name: 'Quick Quiz',
              steps: [
                'Ask students to match network types with their key features.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Network diagrams',
            'Quiz handouts'
          ],
          assessment: [
            'Participation in discussions and quiz results'
          ],
          homework: [
            'Students to find a real-world example of a LAN and a WAN and describe where they might be used'
          ],
          teacherNotes: [
            'Use local examples and scenarios to make the concerns and network types relatable.',
            'Visual aids like diagrams are crucial for understanding network scope.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on differentiating between LAN and WAN. Provide clear, concise definitions and analogies. Use simplified diagrams.',
            '**For advanced learners:** Encourage them to research and explain the technologies that enable these network types (e.g., fiber optics for WANs, cellular networks). Discuss hybrid network setups.'
          ]
        },
        {
          id: 'session4_1.3',
          number: 4,
          title: 'Networking Features and Choice',
          duration: '45 minutes',
          structure: [
            { phase: 'Exploring Network Features', time: '20 min' },
            { phase: 'Case Study: Network Choice', time: '15 min' },
            { phase: 'Class Debate', time: '10 min' }
          ],
          introduction: [
            'Review network types from previous session.'
          ],
          mainActivities: [
            {
              name: 'Network Features Research',
              steps: [
                'In groups, students research and list several key features for each network type.',
                'LAN: high speed, shared resources, limited distance',
                'WAN: lower speed, connects distant LANs, internet as an example',
                'MAN: larger than LAN, often used by cities',
                'Wi-fi: convenience, mobility, limited range, potential security concerns',
                'Each group presents their findings.'
              ]
            },
            {
              name: 'Network Choice Case Studies',
              steps: [
                'Present scenarios of different local organizations (e.g., a school, a small office with multiple branches in the same town, a hotel offering internet access).',
                'For each scenario, guide students to explain and discuss which type of network would be most appropriate and why.',
                'Consider factors like cost, geographical spread, and user needs.'
              ]
            },
            {
              name: 'Class Debate',
              steps: [
                'Pose a question like, "For a small internet cafe in Port Vila, is a LAN with Wi-fi sufficient, or is a MAN necessary?"',
                'Encourage students to debate the pros and cons of each option.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Case study handouts',
            'Group work materials'
          ],
          assessment: [
            'Group presentations on network features',
            'Participation in case study discussions and debate'
          ],
          homework: [
            'Students to choose a local organization and recommend the most suitable network type, justifying their choice'
          ],
          teacherNotes: [
            'Encourage critical thinking and the ability to apply knowledge to real-world scenarios.',
            'Facilitate constructive debate.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a checklist of features for each network type. Offer specific prompts for the case studies. Focus on recommending either a LAN or a WAN.',
            '**For advanced learners:** Ask them to consider the security implications of different network types and suggest specific security measures. They can also research and compare different network hardware (routers, switches).'
          ]
        },
        {
          id: 'session5_1.3',
          number: 5,
          title: 'Software Choices and Installation/Management',
          duration: '45 minutes',
          structure: [
            { phase: 'Open Source vs. Proprietary', time: '10 min' },
            { phase: 'Strengths and Weaknesses', time: '15 min' },
            { phase: 'Online Search & Software Installation', time: '15 min' },
            { phase: 'Justification of Software Choice', time: '5 min' }
          ],
          introduction: [
            'Introduce the concepts of open-source and proprietary software.',
            'Ask students to name examples of each (e.g., Open Office/Libre Office, Windows/Microsoft Office).',
            'Discuss the key differences in licensing, cost, and availability of source code.'
          ],
          mainActivities: [
            {
              name: 'Strengths and Weaknesses',
              steps: [
                'Facilitate a brainstorming session to list the strengths and weaknesses of both open-source and proprietary software.',
                'Open Source: cost-effective, community support, flexibility',
                'Proprietary: dedicated support, user-friendly interface, specific features',
                'Open Source Weaknesses: potential lack of dedicated support, compatibility issues',
                'Proprietary Weaknesses: cost, vendor lock-in'
              ]
            },
            {
              name: 'Software Installation Practical',
              steps: [
                'Demonstrate how to identify software versions.',
                'Explain the importance of updates for security and functionality.',
                'Guide students to download and install a pre-approved, safe open-source software.',
                'Guide students to download and install a trial version of a pre-approved proprietary software.',
                'Demonstrate and have students practice uninstalling software using the operating system\'s uninstall feature.',
                'Discuss the reasons for uninstalling software (e.g., freeing up disk space, removing unwanted programs, resolving software conflicts).'
              ]
            },
            {
              name: 'Software Choice Justification',
              steps: [
                'Briefly revisit the case studies from Session 4.',
                'For each organization, discuss and justify whether open-source or proprietary software might be a better choice and why.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Pre-approved software for installation',
            'Whiteboard/Projector'
          ],
          assessment: [
            'Participation in discussions',
            'Ability to identify and compare software types',
            'Successful installation and uninstallation of software'
          ],
          homework: [
            'Students to research a specific open-source alternative to a popular proprietary software and list its pros and cons for a small business'
          ],
          teacherNotes: [
            'Prioritize pre-approved, safe software downloads.',
            'Supervise the installation and uninstallation process closely.',
            'Emphasize the ethical and legal aspects of software usage and licensing.'
          ],
          differentiation: [
            '**For struggling learners:** Provide direct links to download specific software. Offer step-by-step instructions for installation and uninstallation. Focus on understanding the basic differences and one key strength/weakness of each type.',
            '**For advanced learners:** Ask them to research open-source software with advanced features or explore the concept of software licensing models in more detail (e.g., GPL, MIT). They can also investigate server-side software or operating systems.'
          ]
        }
      ]
    }
  ];

  // Strand 2 Lesson Plans (Web Development)
  const strand2Lessons = [
    {
      id: 'unit2.1',
      unit: 'Unit 2.1',
      title: 'Digital Media Fundamentals',
      weeks: 'Weeks 1-6',
      duration: '7 Sessions (45 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_2.1',
          number: 1,
          title: 'Understanding the Importance of Digital Media',
          duration: '45 minutes',
          structure: [
            { phase: 'Introduction & Brainstorming', time: '10 min' },
            { phase: 'Defining Importance', time: '15 min' },
            { phase: 'Focus on Publications', time: '15 min' },
            { phase: 'Class Discussion', time: '5 min' }
          ],
          introduction: [
            'Begin with a class discussion on how students encounter digital media in their daily lives.',
            'Ask students to brainstorm why digital media is important in today\'s world.'
          ],
          mainActivities: [
            {
              name: 'Defining Importance',
              steps: [
                'Guide students to articulate digital media\'s importance in communication, information sharing, entertainment, education, and business.',
                'Write key points on the board to create a collaborative definition.',
                'Emphasize how digital media enables faster communication across Vanuatu\'s islands.'
              ]
            },
            {
              name: 'Focus on Publications',
              steps: [
                'Discuss the importance of digital media for publications (online newspapers, e-books, digital magazines, websites).',
                'Explore advantages: wider reach, interactivity, multimedia integration, cost-effectiveness, rapid dissemination.',
                'Use examples relevant to Vanuatu (local news websites, digital community announcements).'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Markers/Pens',
            'Examples of digital media content'
          ],
          assessment: [
            'Observation of class participation and contributions to the discussion'
          ],
          teacherNotes: [
            'Encourage students to share personal examples of how they use or consume digital media.',
            'Relate concepts to the local Vanuatu context throughout.'
          ],
          differentiation: [
            '**For struggling learners:** Provide sentence starters for discussions or ask targeted questions.',
            '**For advanced learners:** Challenge them to discuss emerging trends in digital media and their implications for Vanuatu.'
          ]
        },
        {
          id: 'session2_2.1',
          number: 2,
          title: 'Identifying Required Media Devices',
          duration: '45 minutes',
          structure: [
            { phase: 'Audio Device Identification', time: '15 min' },
            { phase: 'Video Device Identification', time: '15 min' },
            { phase: 'Listing Devices for an Event', time: '10 min' },
            { phase: 'Group Sharing', time: '5 min' }
          ],
          introduction: [
            'Present scenarios of specific events (e.g., recording a school choir performance) and discuss what equipment is needed.'
          ],
          mainActivities: [
            {
              name: 'Audio Device Identification',
              steps: [
                'Present event scenarios and ask students to identify necessary audio devices.',
                'Examples: microphones for speeches, recorders for music, headphones for monitoring.',
                'Discuss the basic functions of each device.'
              ]
            },
            {
              name: 'Video Device Identification',
              steps: [
                'Present event scenarios (e.g., filming a school play) and identify video devices needed.',
                'Examples: cameras, tripods, lighting equipment.',
                'Discuss how these devices work together.'
              ]
            },
            {
              name: 'Group Activity',
              steps: [
                'In small groups, students outline required audio and video devices for a complex event (e.g., school cultural festival).',
                'Each group lists devices and their functions.',
                'Groups share their lists with the class.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Examples of digital media devices (images or actual devices if available)'
          ],
          assessment: [
            'Observation of group work and participation',
            'Review of the device lists created by each group'
          ],
          homework: [
            'Research and list 3 common digital media devices used in professional settings and their primary functions'
          ],
          teacherNotes: [
            'If possible, bring in actual audio and video recording equipment to show students.',
            'Use local events as examples (church gatherings, community meetings).'
          ],
          differentiation: [
            '**For struggling learners:** Provide a list of devices to choose from for the scenarios.',
            '**For advanced learners:** Ask them to consider budget constraints or advanced features when selecting devices.'
          ]
        },
        {
          id: 'session3_2.1',
          number: 3,
          title: 'Requirements for Creating Digital Media Content (Audio & Video)',
          duration: '45 minutes',
          structure: [
            { phase: 'Audio Content Requirements', time: '20 min' },
            { phase: 'Video Content Requirements', time: '20 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: [
            'Discuss why planning is important before creating digital media content.'
          ],
          mainActivities: [
            {
              name: 'Audio Content Requirements',
              steps: [
                'Discuss key requirements for good audio content: clear recording, proper microphone placement, noise reduction, scripting, editing.',
                'Present event scenarios and ask students to identify crucial audio requirements.',
                'Examples: recording a podcast, capturing traditional music, making announcements.'
              ]
            },
            {
              name: 'Video Content Requirements',
              steps: [
                'Discuss key requirements for good video content: clear visuals, stable shots, proper lighting, clear audio, scripting/storyboarding, editing.',
                'Present event scenarios and identify crucial video requirements.',
                'Examples: documenting a cultural ceremony, creating promotional videos.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Examples of good and bad audio/video clips'
          ],
          assessment: [
            'Participation in discussions',
            'Ability to identify key requirements for different scenarios'
          ],
          homework: [
            'For a chosen event (e.g., creating a short school announcement video), list 3 key audio requirements and 3 key video requirements'
          ],
          teacherNotes: [
            'Show short examples of well-produced audio and video to illustrate the requirements.',
            'Emphasize the importance of planning before recording.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a checklist of common requirements.',
            '**For advanced learners:** Ask them to consider technical aspects (microphone types, camera settings).'
          ]
        },
        {
          id: 'session4_2.1',
          number: 4,
          title: 'Requirements for Creating Digital Media Content (Graphic Design)',
          duration: '45 minutes',
          structure: [
            { phase: 'Graphic Design Requirements', time: '20 min' },
            { phase: 'Overall Digital Media Requirements', time: '20 min' },
            { phase: 'Q&A', time: '5 min' }
          ],
          introduction: [
            'Introduce graphic design as a crucial component of digital media.',
            'Show examples of effective and ineffective designs.'
          ],
          mainActivities: [
            {
              name: 'Graphic Design Principles',
              steps: [
                'Discuss key graphic design requirements: visual hierarchy, color theory, typography, resolution, branding, target audience.',
                'Present media production examples (posters, social media graphics, website banners).',
                'Students identify and describe key design requirements for each example.',
                'Emphasize cultural relevance for Vanuatu (traditional patterns, local colors).'
              ]
            },
            {
              name: 'Overall Content Requirements',
              steps: [
                'Broaden discussion to overall digital media requirements: purpose, audience, message, platform, technical specifications.',
                'Emphasize cohesive integration of audio, video, and graphics.',
                'Discuss how different requirements work together to create effective media.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Examples of various graphic designs (posters, website banners, social media posts)'
          ],
          assessment: [
            'Participation in discussions',
            'Ability to identify and describe graphic design requirements'
          ],
          homework: [
            'Choose a simple media production (e.g., a poster for a school event) and list 3 key graphic design requirements and 2 overall content requirements'
          ],
          teacherNotes: [
            'Use strong visual examples to highlight the importance of graphic design principles.',
            'Show examples with Vanuatu cultural elements.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on 2-3 core principles like color and readability.',
            '**For advanced learners:** Ask them to analyze and propose improvements to real-world examples.'
          ]
        },
        {
          id: 'session5_2.1',
          number: 5,
          title: 'Using Digital Media Tools (Software) - Opening Files',
          duration: '45 minutes',
          structure: [
            { phase: 'Introduction to Software', time: '5 min' },
            { phase: 'Opening Media Files', time: '25 min' },
            { phase: 'Opening for Specific Purposes', time: '10 min' },
            { phase: 'Describing the Process', time: '5 min' }
          ],
          introduction: [
            'Introduce digital media software that will be used: Audacity (audio), GIMP (images), and optionally OpenShot (video).',
            'Explain that these are free, open-source tools suitable for learning.'
          ],
          mainActivities: [
            {
              name: 'Practical Activity - Opening Files',
              steps: [
                'Guide students through opening various file formats (.wav, .mp3, .jpg, .png) in appropriate software.',
                'Demonstrate the File > Open process in each application.',
                'Students practice opening sample media files provided.',
                'Emphasize navigating to file locations and selecting correct file types.'
              ]
            },
            {
              name: 'Opening for Publications/Websites',
              steps: [
                'Provide scenarios (e.g., opening a logo for a website, opening audio for a podcast).',
                'Students practice opening relevant sample files in the appropriate software.',
                'Discuss file format considerations for different purposes.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Digital media software (Audacity, GIMP, OpenShot)',
            'Sample media files (audio, images, video)'
          ],
          assessment: [
            'Observation of students\' ability to successfully open media files'
          ],
          homework: [
            'Practice opening at least 3 different types of media files in one of the software programs learned'
          ],
          teacherNotes: [
            'Ensure all students have the necessary software installed and accessible.',
            'Provide clear, step-by-step instructions for each software.',
            'Be patient with students who are less familiar with computers.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a printed handout with screenshots of the opening process.',
            '**For advanced learners:** Challenge them to try opening less common file formats.'
          ]
        },
        {
          id: 'session6_2.1',
          number: 6,
          title: 'Using Digital Media Tools (Software) - Basic Modification',
          duration: '45 minutes',
          structure: [
            { phase: 'Basic Audio Modification', time: '20 min' },
            { phase: 'Basic Image Modification', time: '20 min' },
            { phase: 'Describing the Process', time: '5 min' }
          ],
          introduction: [
            'Explain that editing and modifying media is a key skill for creating professional content.',
            'Emphasize the importance of the "Undo" function.'
          ],
          mainActivities: [
            {
              name: 'Audio Editing Practice',
              steps: [
                'Using Audacity, guide students through basic modifications: playing, pausing, selecting audio regions, trimming, and deleting sections.',
                'Demonstrate how to adjust volume levels.',
                'Students practice editing a sample audio file (e.g., removing silence at the beginning/end).'
              ]
            },
            {
              name: 'Image Editing Practice',
              steps: [
                'Using GIMP, guide students through basic modifications: resizing, cropping, and simple color adjustments (brightness, contrast).',
                'Demonstrate the crop tool and resize dialog.',
                'Students practice editing a sample image (e.g., cropping to focus on subject, adjusting brightness).'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Digital media software (Audacity, GIMP)',
            'Sample audio and image files'
          ],
          assessment: [
            'Observation of students\' ability to perform basic modifications'
          ],
          homework: [
            'Modify a downloaded audio file by trimming 5 seconds from the beginning and end',
            'Modify a downloaded image by resizing it to half its original width'
          ],
          teacherNotes: [
            'Encourage students to experiment with the tools.',
            'Remind them to save their work frequently.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on mastering one modification for each type (e.g., trimming audio, cropping images).',
            '**For advanced learners:** Introduce more complex edits like audio fading or applying image filters.'
          ]
        },
        {
          id: 'session7_2.1',
          number: 7,
          title: 'Advanced Modification and Integration',
          duration: '45 minutes',
          structure: [
            { phase: 'Integrated Modification', time: '30 min' },
            { phase: 'Scenario-Based Project', time: '10 min' },
            { phase: 'Wrap-up Discussion', time: '5 min' }
          ],
          introduction: [
            'Explain that professional digital media often combines multiple elements (audio, graphics, video).',
            'Today\'s session will focus on integration skills.'
          ],
          mainActivities: [
            {
              name: 'Integrated Modification',
              steps: [
                'If video editing software is available: Guide students on importing audio and graphics into video editing software, placing them on the timeline, synchronizing elements, and basic video editing (trimming, transitions).',
                'Alternative approach: Focus on integrating audio and graphics for a digital publication using presentation software or simple video tools.',
                'Demonstrate combining multiple media elements to create a cohesive product.'
              ]
            },
            {
              name: 'Practical Scenario',
              steps: [
                'Present a scenario: creating a short promotional video with background music and a logo for a school website or community announcement.',
                'Students, individually or in pairs, plan and attempt to create the integrated media.',
                'Emphasize meeting specific requirements (duration, format, resolution).'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Digital media software (Audacity, GIMP, OpenShot or alternative)',
            'Sample media files (audio, images, video clips)',
            'Scenario description handout'
          ],
          assessment: [
            'Observation of students\' ability to modify and integrate different media types',
            'Review of the final modified media based on the given scenario'
          ],
          homework: [
            'Create a short (15-30 second) video slideshow with background music and a title card using the learned software'
          ],
          teacherNotes: [
            'Emphasize the collaborative nature of media production.',
            'If video editing is too complex, adapt to simpler integration tasks.',
            'Celebrate student creativity and effort.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a pre-made template or break down the scenario into smaller steps.',
            '**For advanced learners:** Encourage more complex projects with multiple transitions, text overlays, or audio effects.'
          ]
        }
      ]
    },
    {
      id: 'unit2.2',
      unit: 'Unit 2.2',
      title: 'Website Development Fundamentals',
      weeks: 'Weeks 1-8',
      duration: '15 Sessions (45 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_2.2',
          number: 1,
          title: 'Web Design Requirements',
          duration: '45 minutes',
          structure: [
            { phase: 'Introduction & Brainstorming', time: '10 min' },
            { phase: 'Identifying Key Requirements', time: '15 min' },
            { phase: 'Case Study Analysis', time: '15 min' },
            { phase: 'Class Discussion', time: '5 min' }
          ],
          introduction: [
            'Begin with a class discussion on why organizations need websites.',
            'Ask students to think about websites they have used for local businesses or organizations in Vanuatu.'
          ],
          mainActivities: [
            {
              name: 'Identifying Key Requirements',
              steps: [
                'Guide students to identify and list basic web design requirements for a small organization.',
                'Requirements: contact information, about us page, services/products, visual appeal, easy navigation, mobile responsiveness.'
              ]
            },
            {
              name: 'Case Study Analysis',
              steps: [
                'Present a simple scenario of a local tourism business with specific requirements (e.g., increasing bookings).',
                'Discuss how web design elements (clear calls to action, image galleries, booking forms) can help meet this requirement.'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Markers/Pens', 'Case study handouts'],
          assessment: ['Observation of class participation', 'Review of requirement lists'],
          homework: ['Identify and list 3 specific web design requirements for a local business they are familiar with'],
          teacherNotes: ['Encourage students to draw on their local Vanuatu context', 'Link business goals to web design features'],
          differentiation: [
            '**For struggling learners:** Provide a checklist of common web design requirements. Focus on identifying just one or two key requirements initially.',
            '**For advanced learners:** Ask them to research and present examples of how specific web design elements have helped organizations achieve goals. Encourage thinking about SEO and analytics.'
          ]
        },
        {
          id: 'session2_2.2',
          number: 2,
          title: 'CSS Introduction and Types',
          duration: '45 minutes',
          structure: [
            { phase: 'Defining CSS', time: '15 min' },
            { phase: 'Types of CSS', time: '15 min' },
            { phase: 'Examples and Discussion', time: '10 min' },
            { phase: 'Quick Quiz', time: '5 min' }
          ],
          introduction: ['Introduce CSS as a stylesheet language used to describe the presentation of HTML documents', 'Explain purpose in separating content from design'],
          mainActivities: [
            {
              name: 'Types of CSS',
              steps: [
                'Explain three types: Inline CSS (within HTML elements), Internal/Embedded CSS (<style> tags in <head>), External CSS (separate .css file).',
                'Emphasize external CSS as preferred method for maintainability.',
                'Show simple examples of each type and discuss advantages/disadvantages.'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Example HTML and CSS code snippets', 'Computers with web browsers'],
          assessment: ['Participation in discussions and quiz results'],
          homework: ['Find examples of Inline, Internal, and External CSS on websites and try to identify them'],
          teacherNotes: ['Use visual aids to demonstrate separation of content and presentation', 'Emphasize why external CSS is industry standard'],
          differentiation: [
            '**For struggling learners:** Focus on basic concept and distinguishing between internal and external CSS. Provide clear, simple examples.',
            '**For advanced learners:** Research CSS preprocessors (Sass, Less) that build upon these concepts.'
          ]
        },
        {
          id: 'session3_2.2',
          number: 3,
          title: 'CSS Syntax and Declarations',
          duration: '45 minutes',
          structure: [
            { phase: 'CSS Syntax', time: '20 min' },
            { phase: 'Examples and Practice', time: '15 min' },
            { phase: 'Sharing and Review', time: '5 min' }
          ],
          introduction: ['Introduce basic CSS syntax components'],
          mainActivities: [
            {
              name: 'CSS Syntax Components',
              steps: [
                'Selector: Targets HTML element(s) to be styled',
                'Declaration Block: Enclosed in curly braces {}, contains declarations',
                'Declaration: Property and value, separated by colon :, ending with semicolon ;',
                'Students practice writing simple CSS declarations for basic styling (text color, background color)'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Markers/Pens', 'Example CSS code', 'Text editors'],
          assessment: ['Observation of ability to identify and write basic CSS syntax'],
          homework: ['Write 3 CSS declarations for different HTML elements (h1, p, body) to change color and font size'],
          teacherNotes: ['Break down syntax into manageable parts', 'Use consistent terminology', 'Provide ample practice opportunities'],
          differentiation: [
            '**For struggling learners:** Provide fill-in-the-blanks exercises. Focus on one property at a time.',
            '**For advanced learners:** Introduce shorthand properties and explore less common CSS properties.'
          ]
        },
        {
          id: 'session4_2.2',
          number: 4,
          title: 'CSS Advantages and Linking with HTML',
          duration: '45 minutes',
          structure: [
            { phase: 'Advantages of CSS', time: '15 min' },
            { phase: 'Linking CSS and HTML', time: '15 min' },
            { phase: 'Demonstration', time: '10 min' },
            { phase: 'Importance of the Link', time: '5 min' }
          ],
          introduction: ['Discuss key benefits of using CSS'],
          mainActivities: [
            {
              name: 'CSS Benefits',
              steps: [
                'Separation of concerns: Keeping design separate from content',
                'Maintainability: Easier to update and modify styles',
                'Consistency: Uniform styling across multiple pages',
                'Accessibility: Cleaner HTML structure',
                'Responsiveness: Different layouts for various devices'
              ]
            },
            {
              name: 'Linking Method',
              steps: [
                'The <link> tag in <head> section links to external CSS files',
                'Attributes: rel="stylesheet" and href="path/to/style.css"',
                'Demonstrate how changes in CSS file affect HTML appearance'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Example HTML and CSS files', 'Computers with browsers and text editors'],
          assessment: ['Understanding of linking process'],
          homework: ['Create simple HTML page and link external CSS file with at least two styles'],
          teacherNotes: ['Visually demonstrate impact of linking CSS', 'Highlight efficiency of external CSS'],
          differentiation: [
            '**For struggling learners:** Focus on <link> tag and href attribute. Provide template.',
            '**For advanced learners:** Discuss loading order and how multiple CSS files can be linked.'
          ]
        },
        {
          id: 'session5_2.2',
          number: 5,
          title: 'External CSS and Basic Webpage',
          duration: '45 minutes',
          structure: [
            { phase: 'Creating External CSS', time: '25 min' },
            { phase: 'Basic HTML with Media', time: '20 min' }
          ],
          introduction: ['Guide students through practical creation of CSS and HTML files'],
          mainActivities: [
            {
              name: 'Practical Activity',
              steps: [
                'Create new text file, save as .css (e.g., styles.css)',
                'Write basic CSS rulesets for headings, paragraphs',
                'Create simple HTML file (index.html) with basic structure',
                'Embed media content (image using <img> tag)',
                'Link styles.css to index.html using <link> tag'
              ]
            }
          ],
          materials: ['Computers with text editors and web browsers', 'Example files', 'Image files for embedding'],
          assessment: ['Review of created .css and .html files for correct syntax and linking'],
          homework: ['Add another media element to HTML page and style it using CSS file'],
          teacherNotes: ['Provide step-by-step instructions', 'Ensure understanding of file naming and folder structures'],
          differentiation: [
            '**For struggling learners:** Provide partially completed files to modify. Focus on one styled element.',
            '**For advanced learners:** Explore different image formats and basic image styling properties (border, padding, margin).'
          ]
        },
        {
          id: 'session6_2.2',
          number: 6,
          title: 'Creating Multiple Webpages',
          duration: '45 minutes',
          structure: [
            { phase: 'Creating Additional Pages', time: '40 min' },
            { phase: 'Discussion', time: '5 min' }
          ],
          introduction: ['Guide students to create multiple linked pages'],
          mainActivities: [
            {
              name: 'Multi-Page Website',
              steps: [
                'Create additional HTML files (about.html, contact.html) with basic content',
                'Embed different media (images or simple audio using <audio> tag)',
                'Ensure each page has proper HTML structure',
                'Link each page to same styles.css file for consistent styling'
              ]
            }
          ],
          materials: ['Computers with text editors and browsers', 'Audio files (optional)', 'Image files'],
          assessment: ['Review of HTML files for correct structure and media embedding'],
          homework: ['Add heading and paragraph to each new page, styled using external CSS'],
          teacherNotes: ['Reinforce code reusability with external CSS', 'Emphasize proper file organization'],
          differentiation: [
            '**For struggling learners:** Focus on creating one additional page. Provide HTML structure templates.',
            '**For advanced learners:** Embed video using <video> tag and explore basic styling for audio/video elements.'
          ]
        },
        {
          id: 'session7_2.2',
          number: 7,
          title: 'Navigation and Linking',
          duration: '45 minutes',
          structure: [
            { phase: 'Adding Navigation', time: '40 min' },
            { phase: 'Testing', time: '5 min' }
          ],
          introduction: ['Guide students to add navigation menus'],
          mainActivities: [
            {
              name: 'Navigation Implementation',
              steps: [
                'Add navigation menus using <nav>, <ul>, <li>, <a> tags',
                'Link between index.html, about.html, and contact.html',
                'Use correct href attributes for page linking',
                'Create direct hyperlinks to embedded media files',
                'Test all hyperlinks to ensure they work correctly'
              ]
            }
          ],
          materials: ['Computers with text editors and browsers', 'Student-created HTML and CSS files'],
          assessment: ['Review of HTML code for correct navigation structure and working hyperlinks'],
          homework: ['Ensure navigation from any page to any other page works, plus hyperlink to one media file'],
          teacherNotes: ['Explain navigation menu structure', 'Stress importance of correct href values and relative paths'],
          differentiation: [
            '**For struggling learners:** Provide pre-built navigation structure to adapt. Focus on linking two pages initially.',
            '**For advanced learners:** Introduce active navigation states in CSS (highlighting current page) and explore different navigation patterns.'
          ]
        },
        {
          id: 'session8_2.2',
          number: 8,
          title: 'Producing a Basic Website',
          duration: '45 minutes',
          structure: [
            { phase: 'Refining the Website', time: '40 min' },
            { phase: 'Peer Review', time: '5 min' }
          ],
          introduction: ['Students improve visual appearance of their multi-page website'],
          mainActivities: [
            {
              name: 'Website Refinement',
              steps: [
                'Apply different CSS properties to headings, paragraphs, navigation, and media elements',
                'Improve overall design using styles.css file',
                'Peer review focusing on navigation and basic styling',
                'Provide constructive feedback'
              ]
            }
          ],
          materials: ['Computers with text editors and browsers', 'Student-created HTML and CSS files'],
          assessment: ['Review of complete website (HTML and CSS) for structure, linking, and styling'],
          homework: ['Complete unfinished styling or add one new content section'],
          teacherNotes: ['Encourage creativity in styling', 'Provide constructive feedback during peer review'],
          differentiation: [
            '**For struggling learners:** Ensure all essential components (pages, navigation, basic styling) are present and functional.',
            '**For advanced learners:** Implement responsive design using CSS media queries or explore Flexbox layouts.'
          ]
        },
        {
          id: 'session9_2.2',
          number: 9,
          title: 'Database-Driven Websites Introduction',
          duration: '45 minutes',
          structure: [
            { phase: 'Introducing Database-Driven Websites', time: '15 min' },
            { phase: 'Major Components', time: '20 min' },
            { phase: 'Analogy', time: '10 min' }
          ],
          introduction: ['Explain dynamic websites where content is retrieved from databases', 'Compare static and dynamic websites'],
          mainActivities: [
            {
              name: 'Key Components',
              steps: [
                'Database: Stores and organizes data (MySQL, PostgreSQL)',
                'DBMS: Software to manage database (phpMyAdmin)',
                'Web Server: Hosts website and processes requests (Apache, Nginx)',
                'Server-Side Scripting: PHP, Python, Node.js for database interaction',
                'Web Browser: Client that requests and displays pages',
                'Use library catalog analogy to explain component interaction'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Markers/Pens', 'Component interaction diagrams'],
          assessment: ['Understanding of core concepts and components'],
          homework: ['Research and list 2 examples of database-driven websites they use and what data might be stored'],
          teacherNotes: ['Use clear, simple language', 'Relate concepts to familiar things', 'Emphasize front-end/back-end separation'],
          differentiation: [
            '**For struggling learners:** Focus on core idea of storing and retrieving information dynamically. Compare to brochure vs. social media feed.',
            '**For advanced learners:** Introduce client-side vs. server-side scripting and role of APIs.'
          ]
        },
        {
          id: 'session10_2.2',
          number: 10,
          title: 'Database Interaction and Tools',
          duration: '45 minutes',
          structure: [
            { phase: 'Interaction Process', time: '25 min' },
            { phase: 'Common Web Programming Tools', time: '15 min' },
            { phase: 'Q&A', time: '5 min' }
          ],
          introduction: ['Explain step-by-step process of database-website interaction'],
          mainActivities: [
            {
              name: 'Interaction Flow',
              steps: [
                '1. User requests webpage',
                '2. Web server receives request',
                '3. Server-side script processes request and queries database',
                '4. Database returns requested data',
                '5. Server-side script formats data into HTML',
                '6. Web server sends HTML to user browser',
                '7. Browser displays dynamic webpage',
                'Introduce tools: PHP with MySQL, Python with Django/Flask',
                'If XAMPP available, introduce Apache, MySQL, PHP components'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Interaction process diagrams', 'Information on web programming tools'],
          assessment: ['Understanding of interaction process and awareness of common tools'],
          homework: ['Research one mentioned programming language (e.g., PHP) and find out why it\'s popular'],
          teacherNotes: ['Use clear diagram to illustrate information flow', 'Keep tool introduction brief, focus on purpose'],
          differentiation: [
            '**For struggling learners:** Focus on sequence of events. Use simple analogies. Highlight one primary tool like PHP.',
            '**For advanced learners:** Discuss role of APIs in connecting systems and how web services enable data exchange.'
          ]
        },
        {
          id: 'session11_2.2',
          number: 11,
          title: 'Database Creation',
          duration: '45 minutes',
          structure: [
            { phase: 'Database Creation (phpMyAdmin)', time: '40 min' }
          ],
          introduction: ['Hands-on database creation using phpMyAdmin'],
          mainActivities: [
            {
              name: 'Practical Database Creation',
              steps: [
                'Access database management software',
                'Create new database',
                'Create simple table with defined columns (fields), data types (TEXT, INT, VARCHAR), and primary keys',
                'Create second related table in same database',
                'Demonstrate concept of relationships between tables (one-to-many if time permits)'
              ]
            }
          ],
          materials: ['Computers with database management software (phpMyAdmin)', 'Example database structure'],
          assessment: ['Review of created database structure and tables'],
          homework: ['Add one more field to a created table and consider appropriate data type'],
          teacherNotes: ['Hands-on practice required', 'Ensure understanding of each component: database, table, fields, data types, primary key'],
          differentiation: [
            '**For struggling learners:** Step-by-step guide with screenshots. Focus on single table with essential fields.',
            '**For advanced learners:** Introduce foreign keys and relationship types (one-to-one, many-to-many). Design database for specific website concept.'
          ]
        },
        {
          id: 'session12_2.2',
          number: 12,
          title: 'Basic Web Programming and Database Connection',
          duration: '45 minutes',
          structure: [
            { phase: 'Server-Side Language Introduction', time: '20 min' },
            { phase: 'Database Connection Script', time: '20 min' }
          ],
          introduction: ['Introduce basic syntax of chosen server-side language (PHP)'],
          mainActivities: [
            {
              name: 'PHP Basics and Connection',
              steps: [
                'Variables, simple output - write basic code examples',
                'Write basic script to connect to database created in previous session',
                'Use database connection functions with credentials (server, username, password, database name)',
                'Emphasize security considerations but keep simple',
                'Test connection and display success/failure message'
              ]
            }
          ],
          materials: ['Computers with text editors and browsers', 'Server-side scripting environment (XAMPP for PHP)', 'Example code snippets'],
          assessment: ['Review of written code for basic syntax and database connection attempts'],
          homework: ['Ensure database connection script successfully connects or identify error'],
          teacherNotes: ['Choose one server-side language and stick with it', 'Focus on core connection concepts', 'Briefly mention security'],
          differentiation: [
            '**For struggling learners:** Provide complete simple connection script to fill in database credentials. Focus on understanding each line.',
            '**For advanced learners:** Introduce error handling for connections and discuss different connection methods (procedural vs. object-oriented).'
          ]
        },
        {
          id: 'session13_2.2',
          number: 13,
          title: 'Database Interaction and Data Entry',
          duration: '45 minutes',
          structure: [
            { phase: 'Simple Data Display', time: '30 min' },
            { phase: 'Data Entry via Web Interface', time: '15 min' }
          ],
          introduction: ['Guide students through retrieving and displaying database data'],
          mainActivities: [
            {
              name: 'Data Display and Entry',
              steps: [
                'Write basic script that retrieves data from database table',
                'Display data on simple webpage',
                'Use SELECT query and iterate through results',
                'If phpMyAdmin available, guide manual data entry into tables',
                'If not, discuss concept of creating web forms for data entry'
              ]
            }
          ],
          materials: ['Computers with text editors, browsers, and server-side environment', 'Database management software (optional)'],
          assessment: ['Observation of ability to retrieve and display database data', 'Understanding of data entry methods'],
          homework: ['Display data from another field in database table, or add new record using phpMyAdmin'],
          teacherNotes: ['For display, focus on simple SELECT query', 'If web forms too complex, focus on DBMS interface for data entry'],
          differentiation: [
            '**For struggling learners:** Provide script that displays specific, pre-defined data. Focus on getting data to appear on page.',
            '**For advanced learners:** Introduce basic filtering or sorting of database results. Discuss SQL injection and basic prevention methods.'
          ]
        },
        {
          id: 'session14_2.2',
          number: 14,
          title: 'Benefits of Database-Driven Websites',
          duration: '45 minutes',
          structure: [
            { phase: 'Discussing Benefits', time: '25 min' },
            { phase: 'Examples', time: '15 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: ['Lead discussion on advantages of database-driven websites'],
          mainActivities: [
            {
              name: 'Key Advantages',
              steps: [
                'Dynamic Content: Easily update and manage website content',
                'Data Organization: Efficiently store and retrieve large amounts of information',
                'User Interaction: Enable user accounts, search, online forms',
                'Scalability: Handle growing data and traffic',
                'Personalization: Provide customized content based on user data',
                'Provide examples of local Vanuatu businesses that could benefit: hotels with booking systems, online marketplaces, service directories'
              ]
            }
          ],
          materials: ['Whiteboard/Projector', 'Markers/Pens'],
          assessment: ['Participation in discussions and understanding of advantages'],
          homework: ['Choose one benefit and explain how a specific local Vanuatu business could leverage it'],
          teacherNotes: ['Connect benefits to practical applications and local Vanuatu context', 'Encourage critical thinking about business operations'],
          differentiation: [
            '**For struggling learners:** Focus on one or two main benefits like easily updating content. Provide concrete examples.',
            '**For advanced learners:** Compare cost-effectiveness of static vs. dynamic websites for different business needs. Discuss security implications of storing user data.'
          ]
        },
        {
          id: 'session15_2.2',
          number: 15,
          title: 'Website Testing and Validation',
          duration: '45 minutes',
          structure: [
            { phase: 'Website Testing', time: '20 min' },
            { phase: 'Website Validation', time: '20 min' },
            { phase: 'Importance Discussion', time: '5 min' }
          ],
          introduction: ['Guide students on comprehensive website testing'],
          mainActivities: [
            {
              name: 'Testing and Validation',
              steps: [
                'Functionality Testing: Check all links work, media loads, correct information displays',
                'Performance Checking: Discuss factors (image size, code efficiency), introduce loading speed tools',
                'Website Validation: Use W3C Markup Validation Service, CSS Validation Service',
                'Demonstrate validation tools usage',
                'Explain validation process: enter URL/upload file, review results, fix errors',
                'Discuss benefits: improved compatibility, accessibility, SEO, performance'
              ]
            }
          ],
          materials: ['Computers with browsers and internet', 'Links to online validation tools (W3C)', 'Student-created websites'],
          assessment: ['Observation of testing and understanding of validation process'],
          homework: ['Validate basic HTML and CSS website using online validator and fix at least one error'],
          teacherNotes: ['Show how to interpret validation errors', 'Emphasize that understanding and fixing errors is the learning process'],
          differentiation: [
            '**For struggling learners:** Focus on functional testing: links work, content displays. Introduce validation as error-finding tool.',
            '**For advanced learners:** Explore browser developer tools for performance analysis, accessibility testing tools, and research different testing types (cross-browser testing).'
          ]
        }
      ]
    }
  ];

  // Strand 3 Lesson Plans
  const strand3Lessons = [
    {
      id: 'unit3.1',
      unit: 'Unit 3.1',
      title: 'Data Representation',
      weeks: 'Weeks 1-8',
      duration: '8 Sessions (45-60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_3.1',
          number: 1,
          title: 'Fundamentals of Digital Representation - Unicode',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Introduction & Discussion', time: '10 min' },
            { phase: 'Understanding Unicode', time: '20 min' },
            { phase: 'Practical Examples', time: '15 min' },
            { phase: 'Summary & Homework', time: '10 min' }
          ],
          introduction: [
            'Begin by asking students how computers store and display text from different languages.',
            'Discuss the challenge of representing diverse writing systems (English, French, Chinese, emojis) in computer systems.'
          ],
          mainActivities: [
            {
              name: 'Understanding Unicode',
              steps: [
                'Explain that Unicode is a universal character encoding standard providing a unique code for every character worldwide.',
                'Discuss the importance of Unicode: global communication, multi-language support, inclusion of symbols and emojis.',
                'Compare Unicode with older standards like ASCII to highlight limitations of ASCII (limited to English/basic characters).',
                'Emphasize Unicode\'s role in legal compliance and international software development.'
              ]
            },
            {
              name: 'Practical Examples',
              steps: [
                'Show examples of Unicode characters including emojis, mathematical symbols, and international scripts.',
                'Demonstrate how Unicode enables consistent text display across different platforms and devices.',
                'Discuss practical applications in Vanuatu\'s multilingual context (Bislama, English, French).'
              ]
            }
          ],
          materials: [
            'Computer/Projector',
            'Internet access for Unicode examples',
            'Handouts with Unicode character tables',
            'Whiteboard/Markers'
          ],
          assessment: [
            'Observation of student participation in discussions',
            'Understanding demonstrated through examples shared'
          ],
          homework: [
            'Explain in 2-3 sentences why Unicode is important for global communication',
            'Research one pre-Unicode character encoding standard and identify its limitations'
          ],
          teacherNotes: [
            'Emphasize the practical importance of Unicode in modern computing.',
            'Relate Unicode to students\' experiences with multilingual text messaging and social media.',
            'Show Unicode character maps online for visual reference.'
          ],
          differentiation: [
            '**For struggling learners:** Provide simplified examples focusing on basic character representation.',
            '**For advanced learners:** Discuss technical aspects like UTF-8, UTF-16 encoding methods and their efficiency.'
          ]
        },
        {
          id: 'session2_3.1',
          number: 2,
          title: 'Number Base Conversions - Binary, Decimal, Hexadecimal',
          duration: '60 minutes',
          structure: [
            { phase: 'Review & Introduction', time: '10 min' },
            { phase: 'Binary to Hexadecimal Conversion', time: '20 min' },
            { phase: 'Decimal Conversions', time: '20 min' },
            { phase: 'Practice & Summary', time: '10 min' }
          ],
          introduction: [
            'Review the concept of number bases (Binary: base-2, Decimal: base-10, Hexadecimal: base-16).',
            'Explain why computers use binary internally and why hexadecimal is useful for humans.'
          ],
          mainActivities: [
            {
              name: 'Binary to Hexadecimal Conversion',
              steps: [
                'Teach the grouping method: Break binary into groups of 4 digits from right, add leading zeros if needed.',
                'Present the conversion table: 0000=0, 0001=1, ..., 1010=A, 1011=B, 1100=C, 1101=D, 1110=E, 1111=F.',
                'Work through examples: 11000011 (binary) → 1100 0011 → C3 (hex).',
                'Practice with students on multiple examples.'
              ]
            },
            {
              name: 'Decimal Conversions',
              steps: [
                'Decimal to Binary: Repeatedly divide by 2, record remainders bottom-up.',
                'Binary to Decimal: Sum (digit × 2^position) starting from right. Example: 110 = (1×4)+(1×2)+(0×1) = 6.',
                'Decimal to Hexadecimal: Repeatedly divide by 16, record remainders bottom-up.',
                'Hexadecimal to Decimal: Sum (digit × 16^position). Example: 7F = (7×16)+(15×1) = 127.',
                'Practice conversions as a class.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Conversion table handouts',
            'Practice worksheets',
            'Calculators'
          ],
          assessment: [
            'Monitor student work during practice problems',
            'Check accuracy of conversion exercises'
          ],
          homework: [
            'Convert 10110110 (binary) to hexadecimal',
            'Convert 42 (decimal) to binary',
            'Convert 2A (hexadecimal) to decimal'
          ],
          teacherNotes: [
            'Students often struggle with hexadecimal letters (A-F). Emphasize that A=10, B=11, C=12, D=13, E=14, F=15.',
            'Use the conversion table as a visual aid throughout the lesson.',
            'Show why binary-to-hex is easier than binary-to-decimal (4-bit grouping vs. complex calculation).'
          ],
          differentiation: [
            '**For struggling learners:** Provide step-by-step worksheets with conversion tables. Start with smaller numbers.',
            '**For advanced learners:** Challenge with larger numbers and ask them to explain why hex is more compact than binary.'
          ]
        },
        {
          id: 'session3_3.1',
          number: 3,
          title: 'Binary Arithmetic - Addition',
          duration: '45-60 minutes',
          structure: [
            { phase: 'Review Number Systems', time: '5 min' },
            { phase: 'Binary Addition Rules', time: '15 min' },
            { phase: 'Practice Problems', time: '20 min' },
            { phase: 'Assessment & Summary', time: '10 min' }
          ],
          introduction: [
            'Review binary number representation.',
            'Ask students how they think computers perform arithmetic operations.'
          ],
          mainActivities: [
            {
              name: 'Binary Addition Rules',
              steps: [
                'Teach the four basic binary addition rules: 0+0=0, 0+1=1, 1+0=1, 1+1=10 (0 with carry 1).',
                'Explain the concept of "carry" in binary addition, similar to decimal addition.',
                'Work through example: 1011 + 0110 = 10001 (show step-by-step with carries).',
                'Emphasize adding from right to left and managing carries.'
              ]
            },
            {
              name: 'Guided Practice',
              steps: [
                'Provide 3-4 examples for students to solve individually.',
                'Walk around to provide assistance and check understanding.',
                'Have volunteers show their work on the board.',
                'Discuss common mistakes and how to avoid them.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Markers',
            'Practice worksheets',
            'Binary addition handouts'
          ],
          assessment: [
            'Accuracy in solving practice problems',
            'Understanding of carry mechanism'
          ],
          homework: [
            'Perform binary addition: 1101 + 0111',
            'Create your own binary addition problem and solve it'
          ],
          teacherNotes: [
            'Students often forget about carries. Use visual aids showing carry positions.',
            'Relate binary addition to decimal addition to build on existing knowledge.',
            'Start with simple 3-4 bit numbers before progressing to larger ones.'
          ],
          differentiation: [
            '**For struggling learners:** Use color-coding for carry bits. Provide problems with fewer bits.',
            '**For advanced learners:** Introduce overflow concept and addition of larger binary numbers.'
          ]
        },
        {
          id: 'session4_3.1',
          number: 4,
          title: 'Binary Arithmetic - Subtraction Using 2\'s Complement',
          duration: '60 minutes',
          structure: [
            { phase: 'Review Binary Addition', time: '5 min' },
            { phase: 'Introduction to 2\'s Complement', time: '20 min' },
            { phase: 'Subtraction Practice', time: '25 min' },
            { phase: 'Summary', time: '10 min' }
          ],
          introduction: [
            'Review binary addition from the previous session.',
            'Introduce the challenge: How do computers perform subtraction?'
          ],
          mainActivities: [
            {
              name: '2\'s Complement Method',
              steps: [
                'Explain that computers use 2\'s complement to convert subtraction into addition.',
                'Teach Step 1a: Find 1\'s complement by inverting all bits of subtrahend.',
                'Teach Step 1b: Add 1 to the 1\'s complement to get 2\'s complement.',
                'Teach Step 2: Add the 2\'s complement to the minuend.',
                'Teach Step 3: Discard the carry from the leftmost bit.',
                'Work through example: 0101 - 0011 (5-3). 2\'s complement of 0011 is 1101. 0101+1101=10010. Discard carry → 0010 (2).'
              ]
            },
            {
              name: 'Guided Practice',
              steps: [
                'Work through 2-3 more examples as a class.',
                'Students solve problems individually with teacher support.',
                'Address common errors: forgetting to discard carry, incorrect 1\'s complement.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            '2\'s complement step-by-step guide handouts',
            'Practice worksheets'
          ],
          assessment: [
            'Correct application of 2\'s complement method',
            'Accuracy in solving subtraction problems'
          ],
          homework: [
            'Perform binary subtraction using 2\'s complement: 1010 - 0111 (show all steps)',
            'Explain in your own words why 2\'s complement method is preferred for subtraction'
          ],
          teacherNotes: [
            '2\'s complement is conceptually challenging. Break it into clear, sequential steps.',
            'Use consistent examples to build pattern recognition.',
            'Emphasize that discarding the carry is a critical final step.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a step-by-step checklist. Use 4-bit numbers only.',
            '**For advanced learners:** Discuss negative number representation using 2\'s complement and sign bits.'
          ]
        },
        {
          id: 'session5_3.1',
          number: 5,
          title: 'Binary Arithmetic - Multiplication (Shift and Add)',
          duration: '60 minutes',
          structure: [
            { phase: 'Review Previous Concepts', time: '5 min' },
            { phase: 'Shift and Add Method', time: '25 min' },
            { phase: 'Practice Problems', time: '20 min' },
            { phase: 'Q&A & Summary', time: '10 min' }
          ],
          introduction: [
            'Review binary addition.',
            'Introduce binary multiplication as a series of additions and shifts.'
          ],
          mainActivities: [
            {
              name: 'Shift and Add Method',
              steps: [
                'Explain the algorithm: For each bit in multiplier (right to left), if bit is 1, write multiplicand shifted left by position; if 0, write zeros.',
                'Work through example: 1011 × 101.',
                'Show: 1011 (bit 0 is 1), 00000 (bit 1 is 0, shifted), 101100 (bit 2 is 1, shifted).',
                'Add all rows: 1011 + 00000 + 101100 = 110111.',
                'Emphasize the pattern: multiplication is repeated addition with positional shifts.'
              ]
            },
            {
              name: 'Structured Practice',
              steps: [
                'Provide 2-3 problems for students to solve in pairs.',
                'Circulate to provide guidance.',
                'Have pairs present their solutions and explain the shifting pattern.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Step-by-step multiplication guide',
            'Practice worksheets'
          ],
          assessment: [
            'Correct application of shift and add method',
            'Understanding of the shifting pattern'
          ],
          homework: [
            'Perform binary multiplication using shift and add: 101 × 11 (show steps)',
            'Solve: 110 × 101'
          ],
          teacherNotes: [
            'Alignment is critical in binary multiplication. Ensure students understand positional shifts.',
            'Relate to decimal long multiplication to help understanding.',
            'Visual alignment on the board helps students grasp the pattern.'
          ],
          differentiation: [
            '**For struggling learners:** Use grid paper to help with alignment. Start with 3-bit numbers.',
            '**For advanced learners:** Challenge with 6-bit multiplication or ask to optimize the algorithm.'
          ]
        },
        {
          id: 'session6_3.1',
          number: 6,
          title: 'Logic Gates - Basic (AND, OR, NOT)',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Logic Gates', time: '10 min' },
            { phase: 'AND Gate', time: '15 min' },
            { phase: 'OR Gate', time: '15 min' },
            { phase: 'NOT Gate', time: '10 min' },
            { phase: 'Summary & Practice', time: '10 min' }
          ],
          introduction: [
            'Explain that logic gates are fundamental building blocks of digital circuits.',
            'Discuss how computers use electrical signals (HIGH=1, LOW=0) to process information.'
          ],
          mainActivities: [
            {
              name: 'AND Gate',
              steps: [
                'Introduce AND gate symbol (D-shape) and function: output is HIGH only if ALL inputs are HIGH.',
                'Present truth table for 2-input AND gate.',
                'Relate to real-world: Two switches in series - light only turns on if both switches are ON.',
                'Work through examples with different input combinations.'
              ]
            },
            {
              name: 'OR Gate',
              steps: [
                'Introduce OR gate symbol (curved shape) and function: output is HIGH if AT LEAST ONE input is HIGH.',
                'Present truth table for 2-input OR gate.',
                'Relate to real-world: Two switches in parallel - light turns on if either switch is ON.',
                'Work through examples.'
              ]
            },
            {
              name: 'NOT Gate',
              steps: [
                'Introduce NOT gate symbol (triangle with circle) and function: output is opposite of input.',
                'Present truth table for NOT gate (single input).',
                'Relate to real-world: Inverter switch.',
                'Emphasize NOT gate only has one input.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Logic gate symbol handouts',
            'Truth table templates',
            'Colored markers for drawing circuits'
          ],
          assessment: [
            'Ability to identify gate symbols',
            'Correct completion of truth tables',
            'Understanding of gate functions'
          ],
          homework: [
            'Draw logic symbols and complete truth tables for 2-input AND, 2-input OR, and NOT gates',
            'Give one real-world example for each gate type'
          ],
          teacherNotes: [
            'Use physical demonstrations (switches and lights) if possible to illustrate gate functions.',
            'Emphasize memorizing gate symbols and their basic functions.',
            'Truth tables are foundational - ensure all students can create them.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-drawn symbols. Focus on completing truth tables with guidance.',
            '**For advanced learners:** Introduce NAND and NOR gates as combinations of basic gates.'
          ]
        },
        {
          id: 'session7_3.1',
          number: 7,
          title: 'Truth Tables for Combined Logic Circuits',
          duration: '60 minutes',
          structure: [
            { phase: 'Review Basic Gates', time: '10 min' },
            { phase: 'Simple Combinations', time: '20 min' },
            { phase: 'Complex 3-Input Circuits', time: '20 min' },
            { phase: 'Practice & Summary', time: '10 min' }
          ],
          introduction: [
            'Review AND, OR, and NOT gate symbols and functions.',
            'Introduce the concept of combining gates to create more complex logic functions.'
          ],
          mainActivities: [
            {
              name: 'Simple 2-Input Combinations',
              steps: [
                'Demonstrate: AND gate followed by NOT gate (NAND).',
                'Teach systematic approach: Create columns for inputs, intermediate outputs, final output.',
                'Work through truth table for (A AND B) then NOT step-by-step.',
                'Show all 4 input combinations (00, 01, 10, 11).'
              ]
            },
            {
              name: 'Complex 3-Input Circuits',
              steps: [
                'Introduce 3-input circuits requiring multiple steps.',
                'Example: (NOT A) OR B, then result AND C.',
                'Teach systematic method: Identify inputs (A, B, C), list all 8 combinations (000 to 111), calculate intermediate outputs, determine final output.',
                'Work through complete example as a class.',
                'Emphasize following the circuit flow from inputs to output.'
              ]
            },
            {
              name: 'Guided Practice',
              steps: [
                'Students work in pairs to construct truth tables for given circuits.',
                'Provide feedback and correct common errors.',
                'Discuss strategies for organizing complex truth tables.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Circuit diagrams handouts',
            'Truth table templates',
            'Colored pens for marking intermediate steps'
          ],
          assessment: [
            'Correct construction of truth tables',
            'Systematic approach to solving complex circuits',
            'Accuracy of intermediate and final outputs'
          ],
          homework: [
            'Create truth table for: (A AND B) then NOT',
            'Describe the steps to create a truth table for: (NOT A) OR B, then AND with C'
          ],
          teacherNotes: [
            'Students often get confused with multiple gates. Emphasize breaking down into steps.',
            'Use color-coding to distinguish intermediate outputs from final outputs.',
            'Practice is essential - provide multiple examples.'
          ],
          differentiation: [
            '**For struggling learners:** Provide partially completed truth tables. Focus on 2-gate combinations first.',
            '**For advanced learners:** Challenge with 4-input circuits or ask to design circuits for specific output patterns.'
          ]
        },
        {
          id: 'session8_3.1',
          number: 8,
          title: 'Review and Assessment Preparation',
          duration: '60 minutes',
          structure: [
            { phase: 'Comprehensive Review', time: '20 min' },
            { phase: 'Practice Assessment', time: '30 min' },
            { phase: 'Q&A and Clarifications', time: '10 min' }
          ],
          introduction: [
            'Overview of all topics covered in Unit 3.1: Unicode, number conversions, binary arithmetic, logic gates.',
            'Explain the assessment structure and expectations.'
          ],
          mainActivities: [
            {
              name: 'Topic Review',
              steps: [
                'Section 1: Quick review of Unicode importance and number base conversions.',
                'Section 2: Review binary addition, subtraction (2\'s complement), multiplication (shift and add).',
                'Section 3: Review logic gate symbols, functions, and truth table construction.',
                'Address any remaining questions from students.'
              ]
            },
            {
              name: 'Practice Assessment',
              steps: [
                'Provide a mini practice test covering all three sections.',
                'Students complete individually under timed conditions.',
                'Review answers as a class, explaining correct solutions.',
                'Identify areas where students need additional support.'
              ]
            }
          ],
          materials: [
            'Practice test handouts',
            'Review notes for all topics',
            'Answer keys',
            'Whiteboard for demonstrations'
          ],
          assessment: [
            'Performance on practice assessment',
            'Identification of weak areas for targeted review'
          ],
          homework: [
            'Complete any unfinished review exercises',
            'Study all notes and examples from Sessions 1-7',
            'Prepare questions for next class'
          ],
          teacherNotes: [
            'This session is crucial for identifying gaps in understanding before the formal assessment.',
            'Provide extra support to struggling students during practice.',
            'Ensure all students have access to review materials.'
          ],
          differentiation: [
            '**For struggling learners:** Provide simplified practice problems. Offer one-on-one support during practice time.',
            '**For advanced learners:** Provide extension questions involving combinations of concepts (e.g., converting results of binary operations to hexadecimal).'
          ]
        }
      ]
    },
    {
      id: 'unit3.2',
      unit: 'Unit 3.2',
      title: 'Introduction to Programming Concepts',
      weeks: 'Weeks 9-12',
      duration: '6 Sessions (60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_3.2',
          number: 1,
          title: 'The Programming Lifecycle',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction & Engagement', time: '10 min' },
            { phase: 'Understanding the Lifecycle', time: '30 min' },
            { phase: 'Group Activity', time: '15 min' },
            { phase: 'Summary & Assessment', time: '5 min' }
          ],
          introduction: [
            'Ask students: "How do you think apps and programs are created? Is it just typing code?"',
            'Discuss the complexity of software development and why planning is essential.',
            'Introduce the idea that programming is a structured process, not random coding.'
          ],
          mainActivities: [
            {
              name: 'Programming Lifecycle Stages',
              steps: [
                'Stage 1: Problem Definition - Clearly understand what the program should do. Ask: What problem are we solving?',
                'Stage 2: Problem Analysis - Break down the problem. Identify inputs, processing steps, and outputs.',
                'Stage 3: Design - Plan the solution using flowcharts or pseudocode. Create a blueprint before coding.',
                'Stage 4: Coding - Write the actual program in a programming language.',
                'Stage 5: Testing - Run the program, find bugs, and ensure it works correctly.',
                'Stage 6: Documentation - Write user manuals, technical documentation, and comments in code.',
                'Stage 7: Maintenance - Update, fix bugs, and add new features over time.'
              ]
            },
            {
              name: 'Real-World Example',
              steps: [
                'Example: Creating a simple calculator app',
                'Problem Definition: Need an app to perform basic arithmetic operations',
                'Analysis: Inputs (two numbers, operation), Processing (calculation), Output (result)',
                'Design: Create flowchart showing input → calculation → output',
                'Discuss why each stage is important and what happens if we skip stages'
              ]
            },
            {
              name: 'Group Activity',
              steps: [
                'Divide class into small groups',
                'Each group selects a simple program idea (e.g., alarm clock, to-do list, quiz game)',
                'Groups work through the first 3 stages of the lifecycle on paper',
                'Each group presents their problem definition and analysis to the class'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Lifecycle diagram handouts',
            'Chart paper for group work',
            'Markers'
          ],
          assessment: [
            'Group presentation quality',
            'Understanding of lifecycle stages demonstrated in group work',
            'Participation in discussions'
          ],
          homework: [
            'Choose a simple program (e.g., password checker, grade calculator)',
            'Write the Problem Definition and Problem Analysis stages',
            'Identify inputs, processing, and expected outputs'
          ],
          teacherNotes: [
            'Emphasize that programming is a PROCESS, not just writing code.',
            'Use analogies: Building a house requires planning (blueprint), not just putting bricks together.',
            'Relate to students\' experience with planning essays or projects.'
          ],
          differentiation: [
            '**For struggling learners:** Provide templates with prompts for each lifecycle stage.',
            '**For advanced learners:** Challenge them to identify potential problems at each stage and how to prevent them.'
          ]
        },
        {
          id: 'session2_3.2',
          number: 2,
          title: 'Problem-Solving and Design Methodologies',
          duration: '60 minutes',
          structure: [
            { phase: 'Review Previous Lesson', time: '5 min' },
            { phase: 'Top-Down Design', time: '20 min' },
            { phase: 'Bottom-Up Design', time: '20 min' },
            { phase: 'Comparison & Practice', time: '15 min' }
          ],
          introduction: [
            'Review the 7 stages of the programming lifecycle.',
            'Focus on the Design stage: "There are different ways to plan a solution."',
            'Introduce two main design approaches: Top-Down and Bottom-Up.'
          ],
          mainActivities: [
            {
              name: 'Top-Down Design (Decomposition)',
              steps: [
                'Definition: Start with the big picture (main problem) and break it into smaller sub-problems.',
                'Example: Designing a School Management System',
                '  - Main System → Student Management, Teacher Management, Class Scheduling',
                '  - Student Management → Enrollment, Grades, Attendance',
                '  - Each sub-problem can be further broken down until simple enough to code',
                'Advantages: Clear structure, easy to understand, good for large projects.',
                'Best for: Large, complex systems with well-defined requirements.'
              ]
            },
            {
              name: 'Bottom-Up Design',
              steps: [
                'Definition: Start with small, basic components and combine them to build the complete system.',
                'Example: Creating a calculator',
                '  - Build individual functions first: add(), subtract(), multiply(), divide()',
                '  - Test each function separately',
                '  - Combine all functions into the main calculator program',
                'Advantages: Components are reusable, easier to test small parts.',
                'Best for: When you have existing code/modules to build upon.'
              ]
            },
            {
              name: 'Comparison Activity',
              steps: [
                'Present scenario: "Design a simple library management system"',
                'Half the class uses Top-Down approach (break into sub-systems)',
                'Half the class uses Bottom-Up approach (identify basic functions first)',
                'Groups present their design approaches',
                'Discuss which approach worked better and why'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Design methodology comparison chart',
            'Case study handouts',
            'Diagram templates'
          ],
          assessment: [
            'Correct application of design methodologies',
            'Quality of problem decomposition',
            'Understanding of when to use each approach'
          ],
          homework: [
            'Scenario: Design a basic online shopping cart system',
            'Create two designs: one using Top-Down and one using Bottom-Up',
            'Write 2-3 sentences explaining which approach you prefer and why'
          ],
          teacherNotes: [
            'Use visual diagrams - tree diagrams for Top-Down, building blocks for Bottom-Up.',
            'Relate to real-life: Top-Down is like planning a trip (destination → routes → stops), Bottom-Up is like cooking (prepare ingredients → combine → final dish).',
            'Emphasize: Both are valid, choice depends on the project and programmer preference.'
          ],
          differentiation: [
            '**For struggling learners:** Provide partially completed diagrams to fill in.',
            '**For advanced learners:** Challenge them to identify which methodology famous software (e.g., Windows OS, mobile apps) might have used.'
          ]
        },
        {
          id: 'session3_3.2',
          number: 3,
          title: 'Representing Solutions - Pseudocode',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Pseudocode', time: '10 min' },
            { phase: 'Pseudocode Conventions', time: '25 min' },
            { phase: 'Writing Practice', time: '20 min' },
            { phase: 'Review & Q&A', time: '5 min' }
          ],
          introduction: [
            'Ask: "How can we describe a program\'s logic without writing actual code?"',
            'Introduce pseudocode: Informal, human-readable description of an algorithm.',
            'Emphasize: Pseudocode is language-independent - focuses on logic, not syntax.'
          ],
          mainActivities: [
            {
              name: 'Pseudocode Structure and Keywords',
              steps: [
                'Common Keywords:',
                '  - START / END - Mark beginning and end of program',
                '  - INPUT / READ - Get data from user',
                '  - OUTPUT / PRINT / DISPLAY - Show results',
                '  - IF...THEN...ELSE - Decision making',
                '  - WHILE / FOR / REPEAT - Loops (repetition)',
                '  - SET / LET - Assign values to variables',
                'Example: Simple program to find the larger of two numbers',
                '  START',
                '  INPUT num1',
                '  INPUT num2',
                '  IF num1 > num2 THEN',
                '    PRINT num1',
                '  ELSE',
                '    PRINT num2',
                '  END IF',
                '  END'
              ]
            },
            {
              name: 'Guidelines for Writing Pseudocode',
              steps: [
                'Use clear, descriptive variable names (age, total, studentName)',
                'Keep it simple and readable',
                'Use indentation to show structure (especially in IF and loops)',
                'One statement per line',
                'Use capital letters for keywords (optional but helps readability)'
              ]
            },
            {
              name: 'Guided Practice',
              steps: [
                'Problem 1: Write pseudocode to calculate the area of a rectangle',
                'Problem 2: Write pseudocode to check if a number is even or odd',
                'Problem 3: Write pseudocode to sum numbers from 1 to 10',
                'Students work individually, teacher circulates to assist',
                'Review solutions as a class'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Pseudocode convention handouts',
            'Practice problem worksheets',
            'Example pseudocode templates'
          ],
          assessment: [
            'Correct use of pseudocode keywords',
            'Logical flow of pseudocode solutions',
            'Proper indentation and formatting'
          ],
          homework: [
            'Write pseudocode for: A program that asks for 3 test scores, calculates the average, and prints "Pass" if average ≥50, else "Fail"',
            'Write pseudocode for: A simple guessing game where the computer picks a number and the user guesses until correct'
          ],
          teacherNotes: [
            'Pseudocode is NOT a programming language - there are no strict rules, but consistency is key.',
            'Focus on LOGIC, not syntax. Students should understand the algorithm.',
            'Common mistake: Students try to write actual code. Remind them it\'s about the logic in plain language.'
          ],
          differentiation: [
            '**For struggling learners:** Provide sentence starters and keyword reference sheets.',
            '**For advanced learners:** Challenge with more complex algorithms (e.g., finding the maximum in a list, simple sorting).'
          ]
        },
        {
          id: 'session4_3.2',
          number: 4,
          title: 'Representing Solutions - Flowcharts',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Flowcharts', time: '10 min' },
            { phase: 'Flowchart Symbols', time: '20 min' },
            { phase: 'Creating Flowcharts', time: '25 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: [
            'Show a complex pseudocode algorithm.',
            'Ask: "Can we represent this visually to make it easier to understand?"',
            'Introduce flowcharts: Visual representation of an algorithm using symbols and arrows.'
          ],
          mainActivities: [
            {
              name: 'Standard Flowchart Symbols',
              steps: [
                'Oval: START / END (also called Terminator)',
                'Parallelogram: INPUT / OUTPUT',
                'Rectangle: PROCESS (calculations, assignments)',
                'Diamond: DECISION (IF statements, yes/no questions)',
                'Arrows: Flow direction (connect symbols)',
                'Display examples of each symbol with labels'
              ]
            },
            {
              name: 'Flowchart Example - Find Larger Number',
              steps: [
                'Draw step-by-step on board:',
                '  1. START (Oval)',
                '  2. INPUT num1, num2 (Parallelogram)',
                '  3. Is num1 > num2? (Diamond)',
                '  4a. Yes → OUTPUT num1 (Parallelogram)',
                '  4b. No → OUTPUT num2 (Parallelogram)',
                '  5. END (Oval)',
                'Emphasize proper use of arrows showing flow',
                'Decision diamonds always have two exits: Yes/No or True/False'
              ]
            },
            {
              name: 'Student Practice',
              steps: [
                'Problem 1: Draw flowchart to check if a number is positive, negative, or zero',
                'Problem 2: Draw flowchart to calculate factorial of a number (using loop)',
                'Students work on paper/whiteboards',
                'Teacher checks for correct symbol usage and flow logic',
                'Display and discuss selected student solutions'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Flowchart symbol reference sheets',
            'Blank flowchart templates',
            'Rulers for drawing neat lines',
            'Colored markers/pencils'
          ],
          assessment: [
            'Correct use of flowchart symbols',
            'Logical flow from start to end',
            'Proper labeling of decision paths',
            'Neatness and readability'
          ],
          homework: [
            'Convert your homework pseudocode (from Session 3) into flowcharts',
            'Create a flowchart for a simple ATM withdrawal process: Check balance → Input amount → If sufficient, dispense → Else, show error'
          ],
          teacherNotes: [
            'Flowcharts are powerful for visualizing logic, especially decisions and loops.',
            'Common mistake: Students forget to label decision branches (Yes/No).',
            'Encourage neatness - messy flowcharts are hard to follow.',
            'Flowcharts and pseudocode serve the same purpose - choosing between them is often a matter of preference.'
          ],
          differentiation: [
            '**For struggling learners:** Provide partially completed flowcharts to finish.',
            '**For advanced learners:** Challenge with nested decisions and multiple loops (e.g., menu-driven program flowchart).'
          ]
        },
        {
          id: 'session5_3.2',
          number: 5,
          title: 'Introduction to Coding and Syntax',
          duration: '60 minutes',
          structure: [
            { phase: 'Pseudocode to Code', time: '15 min' },
            { phase: 'Basic Coding Concepts', time: '25 min' },
            { phase: 'Hands-On Coding', time: '15 min' },
            { phase: 'Debugging Practice', time: '5 min' }
          ],
          introduction: [
            'Show a simple pseudocode algorithm.',
            'Explain: "Now we translate this into actual code that a computer can execute."',
            'Introduce the concept of programming languages and syntax rules.'
          ],
          mainActivities: [
            {
              name: 'From Pseudocode to Code',
              steps: [
                'Pseudocode Example:',
                '  INPUT age',
                '  IF age >= 18 THEN',
                '    PRINT "Adult"',
                '  ELSE',
                '    PRINT "Minor"',
                'Translate to Python code (or chosen language):',
                '  age = int(input("Enter age: "))',
                '  if age >= 18:',
                '      print("Adult")',
                '  else:',
                '      print("Minor")',
                'Explain: Syntax is the grammar of programming - specific rules must be followed.'
              ]
            },
            {
              name: 'Core Coding Concepts',
              steps: [
                'Variables: Containers that store data (age, name, total)',
                'Data Types: Integer, String, Float, Boolean',
                'Operators: +, -, *, /, ==, !=, >, <',
                'Control Structures: IF-ELSE, WHILE, FOR loops',
                'Input/Output: Reading user input, displaying results',
                'Comments: Notes in code that computer ignores (helps humans understand)'
              ]
            },
            {
              name: 'Hands-On Activity',
              steps: [
                'Use computers or online Python environment (e.g., replit.com, Python IDLE)',
                'Students write a simple program:',
                '  - Input two numbers',
                '  - Calculate and display their sum',
                'Extend: Modify program to also show difference, product, and quotient',
                'Teacher demonstrates common syntax errors and how to fix them'
              ]
            }
          ],
          materials: [
            'Computers with Python installed (or online IDE access)',
            'Code example handouts',
            'Syntax reference sheets',
            'Projector for live coding demonstration'
          ],
          assessment: [
            'Successful execution of practice programs',
            'Understanding of variable usage',
            'Ability to identify and fix simple syntax errors'
          ],
          homework: [
            'Write a Python program that asks for your name and age, then prints "Hello [name], you are [age] years old"',
            'Write a program that converts Celsius to Fahrenheit: F = (C × 9/5) + 32'
          ],
          teacherNotes: [
            'Syntax errors are frustrating for beginners. Emphasize: Everyone makes these mistakes, even experts.',
            'Show students how to read error messages - they point to the problem.',
            'Encourage testing after every few lines of code, not just at the end.',
            'Choose a language appropriate for your context (Python recommended for beginners due to readable syntax).'
          ],
          differentiation: [
            '**For struggling learners:** Provide starter code templates with blanks to fill in.',
            '**For advanced learners:** Challenge with conditional logic programs (e.g., simple grade calculator with multiple IF-ELIF-ELSE).'
          ]
        },
        {
          id: 'session6_3.2',
          number: 6,
          title: 'Modular Programming and Functions',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Modularity', time: '10 min' },
            { phase: 'Functions/Procedures', time: '25 min' },
            { phase: 'Creating Modular Programs', time: '20 min' },
            { phase: 'Review & Assessment', time: '5 min' }
          ],
          introduction: [
            'Ask: "What if we need to use the same calculation multiple times in our program? Do we write the same code again and again?"',
            'Introduce modularity: Breaking programs into smaller, reusable pieces called functions or procedures.',
            'Benefits: Code reusability, easier testing, better organization.'
          ],
          mainActivities: [
            {
              name: 'Understanding Functions',
              steps: [
                'Definition: A function is a named block of code that performs a specific task.',
                'Components:',
                '  - Function Name: Descriptive (calculate_area, check_password)',
                '  - Parameters: Inputs the function needs',
                '  - Body: The code that does the work',
                '  - Return Value: Output the function sends back',
                'Example in Python:',
                '  def add_numbers(a, b):',
                '      result = a + b',
                '      return result',
                'Calling the function: sum = add_numbers(5, 3)'
              ]
            },
            {
              name: 'Black Box Concept',
              steps: [
                'Explain: Functions are like "black boxes" - you know what goes in (input) and what comes out (output), but you don\'t need to know HOW it works internally.',
                'Example: Calculator app - you press buttons (input), get result (output), don\'t need to know the internal circuitry.',
                'Advantages: Simplifies program design, allows teamwork (different people write different functions).'
              ]
            },
            {
              name: 'Hands-On: Building a Modular Program',
              steps: [
                'Task: Create a simple calculator program using functions',
                'Students write separate functions:',
                '  - add(x, y)',
                '  - subtract(x, y)',
                '  - multiply(x, y)',
                '  - divide(x, y)',
                'Then create a main program that calls these functions based on user choice',
                'Test each function individually before integrating'
              ]
            }
          ],
          materials: [
            'Computers with programming environment',
            'Function syntax reference sheets',
            'Modular programming examples',
            'Code templates'
          ],
          assessment: [
            'Correct function definition and calling',
            'Proper use of parameters and return values',
            'Successful creation of modular program',
            'Understanding of black box concept'
          ],
          homework: [
            'Create a program with at least 3 functions that work together (e.g., temperature converter with Celsius→Fahrenheit, Fahrenheit→Celsius, Celsius→Kelvin)',
            'Read about programming tools: IDEs, compilers, interpreters (bonus question for test preparation)'
          ],
          teacherNotes: [
            'Modularity is one of the most important programming concepts - emphasize reusability.',
            'Common mistake: Students forget to call the function or pass parameters correctly.',
            'Show real-world analogy: Building a car - engine team, body team, electronics team all work on separate modules that fit together.',
            'This session prepares students for the test - encourage review of all Unit 3.2 concepts.'
          ],
          differentiation: [
            '**For struggling learners:** Provide function templates with TODOs marking where to fill in code.',
            '**For advanced learners:** Introduce concepts like function scope, local vs. global variables, and recursive functions (optional).'
          ]
        }
      ]
    },
    {
      id: 'unit3.3',
      unit: 'Unit 3.3',
      title: 'Microprocessors and Embedded Systems',
      weeks: 'Weeks 13-18',
      duration: '6 Sessions (60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_3.3',
          number: 1,
          title: 'Microprocessor Architecture and Components',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Microprocessors', time: '10 min' },
            { phase: 'Core Components Study', time: '35 min' },
            { phase: 'Component Interaction', time: '10 min' },
            { phase: 'Summary & Assessment', time: '5 min' }
          ],
          introduction: [
            'Ask students: "What is inside a computer that makes it work?"',
            'Introduce the microprocessor as the "brain" of the computer.',
            'Show a physical microprocessor chip if available, or images.',
            'Explain that understanding microprocessor components helps us understand how computers work.'
          ],
          mainActivities: [
            {
              name: 'ALU - Arithmetic Logic Unit',
              steps: [
                'Definition: The component that performs all mathematical calculations and logical operations.',
                'Functions: Addition, subtraction, multiplication, division, AND, OR, NOT, comparisons.',
                'Example: When you calculate 5 + 3 on a calculator app, the ALU performs this operation.',
                'Real-world analogy: The ALU is like a calculator built into the processor.'
              ]
            },
            {
              name: 'Control Unit',
              steps: [
                'Definition: The component that manages and coordinates all activities within the microprocessor.',
                'Functions: Fetches instructions from memory, decodes them, and directs other components.',
                'Example: Like a conductor directing an orchestra, the Control Unit tells each part what to do and when.',
                'Key role: Ensures instructions are executed in the correct sequence.'
              ]
            },
            {
              name: 'Registers',
              steps: [
                'Definition: Small, ultra-fast memory locations inside the microprocessor.',
                'Purpose: Temporarily store data being processed for quick access.',
                'Types: General-purpose registers (store any data), special-purpose registers (specific functions like program counter).',
                'Analogy: Like your desk where you keep papers you\'re currently working on - quick to access.'
              ]
            },
            {
              name: 'System Bus',
              steps: [
                'Definition: Communication pathways connecting the microprocessor to memory and peripherals.',
                'Three types:',
                '  - Address Bus: Carries memory addresses (where to read/write data)',
                '  - Data Bus: Carries actual data being transferred',
                '  - Control Bus: Carries control signals (read, write, reset commands)',
                'Analogy: Like roads (buses) connecting different parts of a city.'
              ]
            },
            {
              name: 'How Components Work Together',
              steps: [
                '1. Control Unit fetches an instruction from memory (via Address Bus)',
                '2. Instruction travels back via Data Bus',
                '3. Control Unit decodes the instruction',
                '4. If calculation needed, ALU performs it using data from Registers',
                '5. Result stored back in Registers or memory',
                '6. Control signals coordinate this entire process via Control Bus'
              ]
            }
          ],
          materials: [
            'Microprocessor architecture diagram',
            'Physical microprocessor chip (if available)',
            'Whiteboard/Projector for drawing',
            'Component interaction flowcharts'
          ],
          assessment: [
            'Students identify components on a diagram',
            'Explain function of at least 2 components',
            'Describe how components interact during instruction execution'
          ],
          homework: [
            'Draw and label a microprocessor architecture diagram',
            'Write 2-3 sentences explaining the function of each major component',
            'Research one real-world microprocessor (e.g., Intel Core, ARM) and identify its specifications'
          ],
          teacherNotes: [
            'Use visual diagrams extensively - this topic is very abstract.',
            'Relate to familiar concepts: Control Unit = conductor, ALU = calculator, Registers = desk.',
            'Common confusion: Students mix up Address Bus vs Data Bus. Use clear examples.',
            'Consider using online simulators to visualize instruction execution.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on the four main components only. Use physical analogies.',
            '**For advanced learners:** Introduce concepts like pipelining, cache memory, and multi-core processors.'
          ]
        },
        {
          id: 'session2_3.3',
          number: 2,
          title: 'From Code to Execution - Compilers, Interpreters, and Assemblers',
          duration: '60 minutes',
          structure: [
            { phase: 'Understanding Code Types', time: '15 min' },
            { phase: 'Translation Tools', time: '30 min' },
            { phase: 'Comparison Activity', time: '10 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: [
            'Show a simple Python program and ask: "Can the computer understand this directly?"',
            'Explain: Computers only understand machine code (binary), but humans write high-level code.',
            'Introduce the need for translation between human-readable code and machine code.'
          ],
          mainActivities: [
            {
              name: 'Types of Code',
              steps: [
                'High-Level Code: Human-readable programming languages (Python, Java, C++)',
                '  - Advantages: Easy to write, understand, and debug',
                '  - Example: print("Hello, World!")',
                'Machine Code: Binary instructions (0s and 1s) that the processor executes',
                '  - Example: 10110000 01100001',
                '  - Directly executed by the microprocessor',
                'Assembly Language: Low-level language using mnemonics',
                '  - Example: MOV AL, 05h',
                '  - One step above machine code, more readable for humans'
              ]
            },
            {
              name: 'Compiler',
              steps: [
                'Definition: Translates entire high-level program into machine code before execution.',
                'Process: Source code → Compiler → Machine code (executable file) → Run',
                'Advantages: Fast execution, optimized code, can run without original source',
                'Disadvantages: Entire program must be error-free before compilation',
                'Example languages: C, C++, Java (to bytecode)',
                'Analogy: Like translating an entire book before reading it'
              ]
            },
            {
              name: 'Interpreter',
              steps: [
                'Definition: Translates and executes code line by line during runtime.',
                'Process: Source code → Interpreter → Execute line 1 → Execute line 2 → ...',
                'Advantages: Immediate feedback, easier debugging, no separate compilation step',
                'Disadvantages: Slower execution, requires interpreter to be present',
                'Example languages: Python, JavaScript, Ruby',
                'Analogy: Like translating a speech sentence by sentence in real-time'
              ]
            },
            {
              name: 'Assembler',
              steps: [
                'Definition: Translates assembly language into machine code.',
                'Purpose: Allows programmers to write low-level code with readable mnemonics',
                'Example:',
                '  Assembly: MOV AL, 05h (Move value 5 into register AL)',
                '  Machine Code: 10110000 00000101',
                'Use cases: Embedded systems, bootloaders, hardware control, performance-critical code',
                'Advantage: Direct hardware control while maintaining some readability'
              ]
            },
            {
              name: 'Comparison Activity',
              steps: [
                'Create a comparison table: Compiler vs Interpreter vs Assembler',
                'Criteria: Translation timing, execution speed, error detection, use cases',
                'Students fill in the table based on discussion',
                'Discuss: Which would you use for a mobile app? An embedded device? A web script?'
              ]
            }
          ],
          materials: [
            'Code samples in different languages',
            'Comparison table handouts',
            'Flowcharts showing translation processes',
            'Videos of compilation/interpretation (optional)'
          ],
          assessment: [
            'Correctly define compiler, interpreter, assembler',
            'Identify advantages and disadvantages of each',
            'Match programming languages to their translation method'
          ],
          homework: [
            'Create a flowchart showing how a Python program gets executed (using an interpreter)',
            'Write 2-3 sentences explaining when you would use assembly language programming',
            'Research: What is bytecode and how does Java use it?'
          ],
          teacherNotes: [
            'Students often confuse compilation with interpretation. Use clear real-time vs pre-translation distinction.',
            'Show actual compilation in action (e.g., gcc compiler) if possible.',
            'Emphasize that choice of tool depends on the application requirements.',
            'Assembly language may seem outdated but is crucial for embedded systems and IoT.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on compiler vs interpreter only. Use simple analogies.',
            '**For advanced learners:** Discuss JIT (Just-In-Time) compilation, bytecode, and hybrid approaches.'
          ]
        },
        {
          id: 'session3_3.3',
          number: 3,
          title: 'Assembly Language and Mnemonic Codes',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Assembly Language', time: '10 min' },
            { phase: 'Mnemonic Codes Study', time: '25 min' },
            { phase: 'Writing Simple Programs', time: '20 min' },
            { phase: 'Review', time: '5 min' }
          ],
          introduction: [
            'Review: Assembly language sits between high-level languages and machine code.',
            'Introduce mnemonics: Shorthand names for machine instructions.',
            'Explain: Assembly is specific to processor architecture (Intel x86, ARM, etc.).'
          ],
          mainActivities: [
            {
              name: 'Common Mnemonic Codes',
              steps: [
                'MOV (Move): Copy data from one location to another',
                '  Example: MOV AL, 05h (Move value 5 into register AL)',
                'ADD (Add): Add two values',
                '  Example: ADD AL, BL (Add contents of BL to AL)',
                'SUB (Subtract): Subtract one value from another',
                '  Example: SUB AL, 03h (Subtract 3 from AL)',
                'JMP (Jump): Change program flow to a different instruction',
                '  Example: JMP LABEL1 (Jump to instruction at LABEL1)',
                'CMP (Compare): Compare two values',
                '  Example: CMP AL, 10h (Compare AL with 10 hex)'
              ]
            },
            {
              name: 'Assembly Language Structure',
              steps: [
                'Instruction format: [Label:] Mnemonic [Operands] [; Comment]',
                'Example:',
                '  START:    MOV AL, 10h    ; Load 10 into AL',
                '            ADD AL, 20h    ; Add 20 to AL',
                '            JMP END        ; Jump to END',
                '  END:      ; Program ends here',
                'Operands can be: Registers (AL, BL), Memory addresses, Immediate values (05h, 10)'
              ]
            },
            {
              name: 'Simple Assembly Programs',
              steps: [
                'Example 1: Addition',
                '  MOV AL, 10h     ; Load 10 into AL',
                '  MOV BL, 20h     ; Load 20 into BL',
                '  ADD AL, BL      ; Add BL to AL (AL now = 30h)',
                '',
                'Example 2: Data Movement',
                '  MOV AL, 05h     ; Load 5 into AL',
                '  MOV BL, AL      ; Copy AL to BL',
                '  ADD AL, 03h     ; Add 3 to AL',
                '',
                'Students trace through instructions step-by-step'
              ]
            },
            {
              name: 'Hands-On Practice',
              steps: [
                'Task 1: Write assembly code to load 15 into AL and 25 into BL, then add them',
                'Task 2: Write assembly code to subtract 5 from 20 and store result',
                'Task 3: Explain what the following code does:',
                '  MOV AL, 0Ah',
                '  ADD AL, 05h',
                '  SUB AL, 03h',
                'Students work individually or in pairs'
              ]
            }
          ],
          materials: [
            'Mnemonic code reference sheets',
            'Assembly language syntax guide',
            'Example programs handouts',
            'Whiteboard for tracing execution'
          ],
          assessment: [
            'Identify common mnemonics and their functions',
            'Write simple assembly code sequences',
            'Trace execution of given assembly programs'
          ],
          homework: [
            'Write assembly code to multiply a number by 2 using only ADD instructions',
            'Explain what the following code does: MOV AL, 64h / SUB AL, 32h',
            'Research: What assembly language does ARM processors use?'
          ],
          teacherNotes: [
            'Assembly is challenging - start with very simple examples.',
            'Use visual aids to show register contents changing as instructions execute.',
            'Hexadecimal notation (05h, 0Ah) can confuse students - review if needed.',
            'Emphasize that assembly is still used in embedded systems and IoT devices.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on MOV and ADD only. Use decimal values instead of hex.',
            '**For advanced learners:** Introduce loops using JMP and conditional jumps (JZ, JNZ).'
          ]
        },
        {
          id: 'session4_3.3',
          number: 4,
          title: 'Programming and Interfacing - Sensors and Actuators',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Interfacing', time: '10 min' },
            { phase: 'Sensors Study', time: '20 min' },
            { phase: 'Actuators Study', time: '20 min' },
            { phase: 'Practical Example', time: '10 min' }
          ],
          introduction: [
            'Ask: "How does a smartphone know its temperature? How does a car airbag deploy?"',
            'Introduce interfacing: Connecting microprocessors to the physical world.',
            'Key concept: Microprocessors need sensors to "sense" and actuators to "act".'
          ],
          mainActivities: [
            {
              name: 'Understanding Sensors',
              steps: [
                'Definition: Devices that detect physical changes and convert them to electrical signals.',
                'Function: Provide INPUT to the microprocessor about the environment.',
                'Common sensors:',
                '  - Temperature sensor: Detects heat (thermistor, LM35)',
                '  - Light sensor: Detects brightness (LDR, photodiode)',
                '  - Motion sensor: Detects movement (PIR sensor)',
                '  - Pressure sensor: Detects force/pressure',
                '  - Switch/Button: Detects user input',
                'Output: Analog voltage or digital signal that microprocessor can read'
              ]
            },
            {
              name: 'Analog-to-Digital Conversion (ADC)',
              steps: [
                'Problem: Sensors often output analog signals (continuous voltage), but microprocessors process digital (0s and 1s).',
                'Solution: ADC (Analog-to-Digital Converter) converts analog to digital.',
                'Example: Temperature sensor outputs 0.5V for 25°C → ADC converts to binary value → Microprocessor reads it as number',
                'ADC is often built into microcontrollers (like Arduino, Raspberry Pi Pico)'
              ]
            },
            {
              name: 'Understanding Actuators',
              steps: [
                'Definition: Devices that receive signals from microprocessor and produce physical action.',
                'Function: Provide OUTPUT - microprocessor controls the environment.',
                'Common actuators:',
                '  - LED: Light output (visual indicator)',
                '  - Motor: Rotational movement (fans, wheels, pumps)',
                '  - Buzzer/Speaker: Sound output',
                '  - Servo: Precise angle control (robotic arms, gates)',
                '  - Relay: Switch high-power devices on/off',
                'Input: Digital signal or PWM (Pulse Width Modulation) from microprocessor'
              ]
            },
            {
              name: 'Sensor-Microprocessor-Actuator System',
              steps: [
                'Flow: Sensor → ADC → Microprocessor (processes) → Actuator',
                'Example 1: Automatic Light System',
                '  - Light sensor detects darkness → Microprocessor reads low value → Turns LED ON',
                'Example 2: Temperature Control',
                '  - Temperature sensor detects heat → Microprocessor reads high value → Activates fan (motor)',
                'Example 3: Security Alarm',
                '  - Motion sensor detects movement → Microprocessor processes → Activates buzzer'
              ]
            },
            {
              name: 'English Instructions Example',
              steps: [
                'Task: Control an LED based on a switch state',
                'Instructions in English:',
                '  1. Read the current state of the digital input pin connected to the switch',
                '  2. If switch is pressed (HIGH), turn LED ON (set output pin to HIGH)',
                '  3. If switch is not pressed (LOW), turn LED OFF (set output pin to LOW)',
                'This can be converted to mnemonic code or high-level language'
              ]
            }
          ],
          materials: [
            'Sensor and actuator samples (if available)',
            'Circuit diagrams showing connections',
            'Videos of sensor/actuator operation',
            'Microcontroller demonstration (Arduino/Raspberry Pi)'
          ],
          assessment: [
            'Differentiate between sensors and actuators',
            'Explain role of ADC in interfacing',
            'Describe sensor-microprocessor-actuator flow for a real scenario'
          ],
          homework: [
            'List 3 sensors and 3 actuators found in a modern smartphone',
            'Describe the interfacing process for a temperature sensor connected to a microprocessor',
            'Write English instructions for: "If temperature > 30°C, turn on fan"'
          ],
          teacherNotes: [
            'Physical demonstrations are highly effective for this topic if equipment available.',
            'Relate to Vanuatu context: solar panel controllers, water pump systems, weather stations.',
            'ADC concept can be abstract - use voltage-to-number examples.',
            'Emphasize real-world applications to maintain student interest.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on simple sensor/actuator examples (switch + LED).',
            '**For advanced learners:** Discuss PWM for motor speed control, I2C/SPI communication protocols.'
          ]
        },
        {
          id: 'session5_3.3',
          number: 5,
          title: 'Embedded Systems and Real-World Applications',
          duration: '60 minutes',
          structure: [
            { phase: 'Defining Embedded Systems', time: '15 min' },
            { phase: 'Examples and Applications', time: '25 min' },
            { phase: 'Importance and Economics', time: '15 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: [
            'Ask: "What devices do you use daily that have computers inside?"',
            'List responses: Phones, TVs, cars, washing machines, calculators.',
            'Explain: These all contain embedded systems - computers designed for specific tasks.'
          ],
          mainActivities: [
            {
              name: 'Embedded System Definition',
              steps: [
                'Definition: A specialized computer system designed to perform dedicated functions within a larger device.',
                'Key characteristics:',
                '  - Purpose-specific (not general-purpose like a PC)',
                '  - Embedded within larger systems',
                '  - Often real-time operation requirements',
                '  - Resource-constrained (limited memory, power)',
                '  - Reliable and efficient',
                'Components: Microprocessor/microcontroller, memory, input/output interfaces, power supply'
              ]
            },
            {
              name: 'Embedded vs General-Purpose Computers',
              steps: [
                'General-Purpose (PC, laptop):',
                '  - Run many different applications',
                '  - User can install new software',
                '  - Powerful processors, lots of memory',
                '  - Flexible but expensive',
                'Embedded (washing machine controller):',
                '  - Run ONE specific application',
                '  - Software pre-installed, not user-changeable',
                '  - Simpler processor, limited memory',
                '  - Optimized for specific task, cost-effective'
              ]
            },
            {
              name: 'Examples of Embedded Systems',
              steps: [
                'Vanuatu-relevant examples:',
                '  - Mobile Phones: Communication, apps, camera control',
                '  - Solar Inverters: Convert DC to AC for home electricity',
                '  - Digital Televisions: Signal processing, smart features',
                '  - ATMs: Banking transactions, cash dispensing',
                '  - Modern Vehicles: Engine control, ABS, airbags',
                'Other examples:',
                '  - Medical devices: Heart rate monitors, insulin pumps',
                '  - Industrial: Factory automation, robotics',
                '  - Consumer: Digital cameras, smart watches, game consoles'
              ]
            },
            {
              name: 'Importance of Programmable Microprocessors in Embedded Systems',
              steps: [
                'Flexibility: Behavior can be changed through software updates without hardware changes.',
                '  Example: Smartphone features improved through updates',
                'Automation: Can perform complex tasks automatically based on pre-programmed logic.',
                '  Example: Car cruise control maintains speed automatically',
                'Cost-effectiveness: One hardware design can serve multiple products with different software.',
                '  Example: Same microcontroller used in different appliances',
                'Adaptability: Can respond to different conditions and user needs.',
                '  Example: Air conditioner adjusts based on temperature sensor'
              ]
            },
            {
              name: 'Economic Importance',
              steps: [
                'Market size: Embedded systems are in billions of devices worldwide.',
                'Innovation driver: Enable new products and services.',
                '  Example: Smart home devices, wearable health monitors',
                'Job creation: Demand for embedded systems engineers, IoT developers.',
                'Vanuatu context: Solar power systems, mobile banking, agricultural technology.',
                'Future growth: IoT (Internet of Things) expanding rapidly.'
              ]
            },
            {
              name: 'Software Reliability Critical',
              steps: [
                'Why reliability matters: Embedded devices often control critical functions.',
                'Examples of consequences:',
                '  - Medical device bug → Incorrect medication dosage → Patient harm',
                '  - Car airbag software error → Fails to deploy → Safety risk',
                '  - Banking system bug → Incorrect transactions → Financial loss',
                'Testing importance: Extensive testing required before deployment.',
                'Safety-critical systems: Aviation, medical, automotive - require certification.'
              ]
            }
          ],
          materials: [
            'Examples of embedded devices (physical or images)',
            'Case studies of embedded system failures',
            'Videos showing embedded systems in action',
            'Industry statistics on embedded systems market'
          ],
          assessment: [
            'Define embedded system with examples',
            'Explain importance of programmable microprocessors',
            'Discuss consequences of software errors in critical applications'
          ],
          homework: [
            'Identify 5 embedded systems in your home or community',
            'Research one safety-critical embedded system and its testing requirements',
            'Write 2-3 sentences on why embedded systems are important for Vanuatu\'s development'
          ],
          teacherNotes: [
            'Use local examples to make content relevant to students.',
            'Discuss both benefits and risks of embedded systems.',
            'Emphasize career opportunities in this growing field.',
            'Software reliability discussion can lead to broader ethics conversation.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on identifying embedded systems in daily life with simple definitions.',
            '**For advanced learners:** Discuss IoT, Industry 4.0, and future trends in embedded systems.'
          ]
        },
        {
          id: 'session6_3.3',
          number: 6,
          title: 'Circuit Diagrams and Review',
          duration: '60 minutes',
          structure: [
            { phase: 'Understanding Circuit Diagrams', time: '20 min' },
            { phase: 'Microprocessor Circuit Components', time: '20 min' },
            { phase: 'Comprehensive Review', time: '15 min' },
            { phase: 'Test Preparation', time: '5 min' }
          ],
          introduction: [
            'Show a simple circuit diagram for a microprocessor system.',
            'Explain: Circuit diagrams are blueprints showing how components connect.',
            'Goal: Understand essential components needed to program a microprocessor.'
          ],
          mainActivities: [
            {
              name: 'Circuit Diagram Basics',
              steps: [
                'Purpose: Visual representation of electrical connections.',
                'Key components shown:',
                '  - Microprocessor/Microcontroller chip',
                '  - Power supply (VCC, GND)',
                '  - Clock/Oscillator (timing)',
                '  - Programming interface (USB, UART)',
                '  - Input/Output pins',
                '  - Reset circuitry',
                'Diagrams divided into functional sections for clarity'
              ]
            },
            {
              name: 'Essential Components for Programming',
              steps: [
                'Power Supply Section:',
                '  - Voltage regulator: Provides stable voltage (e.g., 5V or 3.3V)',
                '  - Capacitors: Smooth voltage fluctuations',
                '  - VCC (positive) and GND (ground) connections',
                'Clock Section:',
                '  - Crystal oscillator or resonator: Provides timing signals',
                '  - Typically 8MHz, 16MHz, or other frequencies',
                '  - Essential for instruction execution timing',
                'Programming Interface:',
                '  - USB-to-serial converter (common in Arduino)',
                '  - UART pins (TX, RX) for data transfer',
                '  - Or specialized programmers (ICSP, JTAG)',
                'I/O Connections:',
                '  - Digital pins for sensors/actuators',
                '  - Analog pins with ADC for sensors',
                'Reset Circuit:',
                '  - Button to restart microprocessor',
                '  - Pull-up resistor for stable operation'
              ]
            },
            {
              name: 'Reading a Simple Circuit Diagram',
              steps: [
                'Example: Arduino-like microcontroller circuit',
                'Identify:',
                '  - Microcontroller chip (center)',
                '  - Power connections (VCC to 5V, GND)',
                '  - USB connector for programming',
                '  - Crystal for clock (16MHz)',
                '  - Reset button',
                '  - LED on pin 13 (output example)',
                'Trace connections: Power → Chip → USB → External components'
              ]
            },
            {
              name: 'Unit 3.3 Comprehensive Review',
              steps: [
                'Section 1: Microprocessor Architecture',
                '  - ALU, Control Unit, Registers, System Bus',
                '  - Functions and interactions',
                'Section 2: Code to Execution',
                '  - High-level → Machine code',
                '  - Compilers, Interpreters, Assemblers',
                'Section 3: Programming & Interfacing',
                '  - Mnemonic codes (MOV, ADD, JMP)',
                '  - Sensors and Actuators',
                '  - English instructions',
                'Section 4: Embedded Systems',
                '  - Definition and examples',
                '  - Importance and economics',
                '  - Software reliability'
              ]
            },
            {
              name: 'Test Preparation Tips',
              steps: [
                'Review all notes from Sessions 1-6',
                'Practice identifying microprocessor components',
                'Understand difference between compiler, interpreter, assembler',
                'Know common mnemonic codes',
                'Be able to explain sensor-microprocessor-actuator flow',
                'Memorize embedded system definition with examples',
                'Prepare for MCQ, matching, and short answer questions'
              ]
            }
          ],
          materials: [
            'Circuit diagrams handouts',
            'Microcontroller board (Arduino, Raspberry Pi Pico) if available',
            'Review notes from all Unit 3.3 sessions',
            'Practice questions'
          ],
          assessment: [
            'Identify components on a circuit diagram',
            'Explain purpose of each circuit section',
            'Demonstrate readiness for Unit 3.3 test'
          ],
          homework: [
            'Review all Unit 3.3 notes',
            'Complete practice questions provided',
            'Prepare questions for clarification in next class',
            'Study the test blueprint to understand assessment structure'
          ],
          teacherNotes: [
            'Circuit diagrams can be intimidating - focus on functional understanding, not detailed electronics.',
            'Use colored markers to highlight different sections of the circuit.',
            'This session is crucial for test preparation - ensure all concepts are clear.',
            'Allocate time for student questions and clarifications.'
          ],
          differentiation: [
            '**For struggling learners:** Provide simplified circuit diagram with clear labels. Focus on key concepts only.',
            '**For advanced learners:** Challenge with more complex circuits, discuss PCB design or simulation tools.'
          ]
        }
      ]
    }
  ];

  // Strand 4 Lesson Plans
  const strand4Lessons = [
    {
      id: 'unit4.1',
      unit: 'Unit 4.1',
      title: 'Preventing Cyber Attacks and Threats',
      weeks: 'Weeks 1-4',
      duration: '4 Sessions (45-60 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_4.1',
          number: 1,
          title: 'Understanding Cybercrime and Cyber Legislation',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Cybercrime', time: '15 min' },
            { phase: 'Cyber Legislation Overview', time: '20 min' },
            { phase: 'Vanuatu Context Discussion', time: '15 min' },
            { phase: 'Summary & Homework', time: '10 min' }
          ],
          introduction: [
            'Begin by asking students about their online experiences and awareness of cyber threats.',
            'Show recent news headlines about cybercrimes in Vanuatu and the Pacific region.',
            'Define cybercrime: Criminal activity targeting or using computers, networks, or networked devices.'
          ],
          mainActivities: [
            {
              name: 'Understanding Cybercrime',
              steps: [
                'Explain the definition of cybercrime and its various forms.',
                'Discuss common cyber threats: ransomware attacks, data breaches, phishing, cyberbullying, online defamation.',
                'Show real-world examples relevant to Vanuatu and Pacific Island nations.',
                'Emphasize the growing concerns in ICT systems across education, health, and government sectors.'
              ]
            },
            {
              name: 'Cyber Legislation in Vanuatu',
              steps: [
                'Introduce the concept of cyber legislation: laws addressing cybercrime and promoting secure internet use.',
                'Discuss the Harmful Digital Communications Act in Vanuatu and its purpose.',
                'Explain key components: definitions of cyber offenses, penalties and punishments, establishment of specific offenses.',
                'Compare with cyber laws in other Pacific Island nations.',
                'Discuss challenges: lack of proper legislation, limited technical capacity, resource constraints.'
              ]
            }
          ],
          materials: [
            'Computer/Projector for news examples',
            'Handouts with cyber legislation overview',
            'Case study examples from Vanuatu',
            'Whiteboard/Markers'
          ],
          assessment: [
            'Participation in class discussions',
            'Understanding of cybercrime definitions',
            'Ability to identify local examples'
          ],
          homework: [
            'Research and list 3 cybercrimes reported in Vanuatu or the Pacific region in the past year.',
            'Write a short paragraph explaining why cyber legislation is important for Vanuatu.'
          ],
          teacherNotes: [
            'Emphasize the relevance to students\' daily internet use and social media activities.',
            'Relate legislation to real consequences students might face for harmful digital communications.',
            'Consider inviting a guest speaker from CERT Vanuatu if possible.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-written lists of cybercrime types with simple definitions.',
            '**For advanced learners:** Ask them to analyze the strengths and weaknesses of current Vanuatu cyber legislation.'
          ]
        },
        {
          id: 'session2_4.1',
          number: 2,
          title: 'Common Cyber Security Attacks and Preventative Measures',
          duration: '60 minutes',
          structure: [
            { phase: 'Review & Attack Types', time: '15 min' },
            { phase: 'Preventative Measures Workshop', time: '30 min' },
            { phase: 'Group Presentations', time: '10 min' },
            { phase: 'Summary', time: '5 min' }
          ],
          introduction: [
            'Review cybercrime concepts from previous session.',
            'Ask students if they or anyone they know has experienced a cyber attack.',
            'Introduce the session focus: identifying threats and learning prevention strategies.'
          ],
          mainActivities: [
            {
              name: 'Common Cyber Security Attacks',
              steps: [
                'Phishing Attacks: Fraudulent attempts to obtain sensitive information via fake emails or websites.',
                'Malware: Malicious software designed to harm or exploit computer systems (viruses, trojans, ransomware).',
                'Denial-of-Service (DoS/DDoS): Overwhelming a server or network with traffic to make it unavailable.',
                'Identity Theft: Unauthorized use of personal information for fraudulent purposes.',
                'Social Engineering: Manipulating people into revealing confidential information.',
                'Show visual examples of each attack type with Pacific context.'
              ]
            },
            {
              name: 'Preventative Measures Workshop',
              steps: [
                'Divide class into groups, assign each group one preventative measure.',
                'Group 1: Strong, Unique Passwords - Combination of letters, numbers, symbols.',
                'Group 2: Keep Software Updated - Regular updates patch vulnerabilities.',
                'Group 3: Be Wary of Suspicious Links - Recognize phishing attacks.',
                'Group 4: Back Up Data Regularly - Secure backups for recovery.',
                'Group 5: Multi-Factor Authentication (MFA) - Extra layer of security.',
                'Group 6: Use VPN - Encrypts internet traffic.',
                'Each group creates a 2-minute presentation with examples relevant to Vanuatu.'
              ]
            }
          ],
          materials: [
            'Computers/tablets for research',
            'Chart paper for group presentations',
            'Markers and pens',
            'Phishing email examples (printed handouts)',
            'Password strength checker tool (online)'
          ],
          assessment: [
            'Quality of group presentations',
            'Understanding of preventative measures',
            'Practical application to local context'
          ],
          homework: [
            'Check your own passwords and rate their strength (without sharing them).',
            'Enable two-factor authentication on at least one of your social media accounts.',
            'Write one paragraph about how multi-factor authentication adds security.'
          ],
          teacherNotes: [
            'Emphasize practical, actionable steps students can take immediately.',
            'Discuss the limited internet connectivity in some parts of Vanuatu and how it affects security updates.',
            'Consider demonstrating a live phishing email example (generic, not targeting anyone).'
          ],
          differentiation: [
            '**For struggling learners:** Focus on one preventative measure (passwords) with clear, simple guidelines.',
            '**For advanced learners:** Challenge them to create a security policy for a small Vanuatu business.'
          ]
        },
        {
          id: 'session3_4.1',
          number: 3,
          title: 'Cybersecurity Tools and Solutions',
          duration: '60 minutes',
          structure: [
            { phase: 'Introduction to Security Tools', time: '10 min' },
            { phase: 'Tool Categories Exploration', time: '35 min' },
            { phase: 'Practical Demo', time: '10 min' },
            { phase: 'Summary & Discussion', time: '5 min' }
          ],
          introduction: [
            'Review preventative measures from the previous session.',
            'Ask students: "What tools or software do you think can protect against cyber attacks?"',
            'Explain that cybersecurity tools are essential for implementing preventative measures effectively.'
          ],
          mainActivities: [
            {
              name: 'Antivirus & Anti-Malware Software',
              steps: [
                'Bitdefender: Advanced threat detection, real-time protection.',
                'Norton Security: Comprehensive malware and phishing protection.',
                'Malwarebytes: Specializes in detecting and removing malware.',
                'ESET NOD32: Lightweight and effective antivirus.',
                'Discuss how these tools scan, detect, and remove malicious software.',
                'Emphasize the importance of keeping antivirus software updated.'
              ]
            },
            {
              name: 'Network Security Tools',
              steps: [
                'Wireshark: Packet analyzer for monitoring network traffic.',
                'Snort: Open-source intrusion detection system.',
                'Fortinet SIEM: For detecting and responding to security threats.',
                'Explain how network security tools monitor and protect organizational networks.',
                'Discuss relevance for Vanuatu businesses and government institutions.'
              ]
            },
            {
              name: 'Password Management & Endpoint Protection',
              steps: [
                'Password Managers: Keeper Security, Bitwarden (secure password storage).',
                'Endpoint Protection: McAfee MVision Cloud, VMware Endpoint Protection.',
                'Penetration Testing: Kali Linux, Metasploit (ethical hacking tools).',
                'Explain how these tools provide comprehensive security coverage.',
                'Discuss the role of IT professionals in using these advanced tools.'
              ]
            }
          ],
          materials: [
            'Computer lab with internet access',
            'Demo versions of antivirus software',
            'Handouts with tool categories and descriptions',
            'Projector for demonstrations',
            'Video tutorials on cybersecurity tools'
          ],
          assessment: [
            'Participation in discussions',
            'Understanding of different tool categories',
            'Ability to match tools to security needs'
          ],
          homework: [
            'Research one free antivirus software suitable for Vanuatu users and write a short review (features, pros, cons).',
            'Install a password manager on your phone or computer and create a secure password for one account.'
          ],
          teacherNotes: [
            'Some tools mentioned (like Kali Linux) are advanced - explain they require specialized training.',
            'Focus on tools accessible to students (free antivirus, password managers).',
            'Consider discussing cost implications for Vanuatu families and businesses.',
            'Emphasize that free tools can provide good basic protection.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on one category (antivirus) with simple installation and use instructions.',
            '**For advanced learners:** Introduce them to basic penetration testing concepts and ethical hacking careers.'
          ]
        },
        {
          id: 'session4_4.1',
          number: 4,
          title: 'Combating Cybercrimes - Efforts and Challenges in the Pacific',
          duration: '60 minutes',
          structure: [
            { phase: 'Review & Context Setting', time: '10 min' },
            { phase: 'Global and Local Efforts', time: '20 min' },
            { phase: 'Pacific Challenges Discussion', time: '20 min' },
            { phase: 'Action Planning & Summary', time: '10 min' }
          ],
          introduction: [
            'Review key concepts from previous sessions: attacks, prevention, tools.',
            'Ask: "Who is responsible for fighting cybercrime - individuals, organizations, or governments?"',
            'Explain that combating cybercrime requires coordinated efforts at all levels.'
          ],
          mainActivities: [
            {
              name: 'Efforts to Combat Cybercrimes',
              steps: [
                'Computer Emergency Response Team (CERT): Role in responding to security incidents.',
                'CERT Vanuatu: Specific initiatives and contact information.',
                'International Collaborations: Pacific Islands working together on cybersecurity.',
                'Strengthening Legal Frameworks: Developing and updating cyber legislation.',
                'Capacity Building and Training: Workshops for government, business, and individuals.',
                'Public Awareness and Education: Campaigns to educate citizens about cyber threats.',
                'Discuss the Pacific Cyber Security Operational Network (PaCSON).'
              ]
            },
            {
              name: 'Challenges for Pacific Island Nations',
              steps: [
                'Lack of Proper Legislation: Many nations still developing comprehensive cyber laws.',
                'Limited Technical Capacity: Shortage of trained cybersecurity professionals.',
                'Resource Constraints: Limited funding for cybersecurity infrastructure and training.',
                'Low Awareness Levels: Many citizens unaware of cyber threats and prevention.',
                'Geographical Challenges: Scattered islands make coordination difficult.',
                'Internet Penetration Gaps: Not all communities have reliable internet access.',
                'Facilitate a discussion: How can Vanuatu overcome these challenges?',
                'Brainstorm practical solutions appropriate for the Pacific context.'
              ]
            }
          ],
          materials: [
            'Information sheets about CERT Vanuatu',
            'Case studies of successful cybersecurity initiatives in the Pacific',
            'Chart paper for brainstorming',
            'Video on international cybersecurity cooperation (if available)',
            'Guest speaker from CERT Vanuatu or IT department (if possible)'
          ],
          assessment: [
            'Participation in discussions and brainstorming',
            'Understanding of challenges facing Pacific nations',
            'Quality of proposed solutions'
          ],
          homework: [
            'Write a one-page proposal for improving cybersecurity awareness in your community or school.',
            'Prepare for the upcoming assignments by selecting your topic of interest.'
          ],
          teacherNotes: [
            'Emphasize that students can be cybersecurity advocates in their communities.',
            'Discuss career opportunities in cybersecurity for Pacific Islanders.',
            'Connect CERT Vanuatu contact info for students interested in internships or training.',
            'Emphasize the importance of reporting cybercrimes to proper authorities.'
          ],
          differentiation: [
            '**For struggling learners:** Provide a structured template for the cybersecurity awareness proposal.',
            '**For advanced learners:** Challenge them to research and present on a specific international cybersecurity framework or treaty.'
          ]
        }
      ]
    },
    {
      id: 'unit4.2',
      unit: 'Unit 4.2',
      title: 'Ethical Measures in ICT',
      weeks: 'Weeks 5-7',
      duration: '7 Sessions (45 min each)',
      linkedToScheme: true,
      linkedToBlueprint: true,
      sessions: [
        {
          id: 'session1_4.2',
          number: 1,
          title: 'Defining Cybercrime and Cyber Legislation',
          duration: '45 minutes',
          structure: [
            { phase: 'Introduction to Cybercrime', time: '20 min' },
            { phase: 'Introducing Cyber Legislation', time: '20 min' },
            { phase: 'Local Examples', time: '5 min' }
          ],
          introduction: [
            'Begin with a class discussion on types of illegal activities committed using computers and the internet.',
            'Guide students to formulate a definition of cybercrime as criminal activities using digital technologies.',
            'Ask students if they\'ve heard of incidents in Vanuatu involving online scams or social media hacking.'
          ],
          mainActivities: [
            {
              name: 'Introduction to Cybercrime',
              steps: [
                'Discuss illegal activities online: hacking, online fraud, cyberbullying.',
                'Guide students to define cybercrime: criminal activity targeting or using computers, networks, or devices.',
                'Vanuatu-specific example: People tricked into sending money online or hacked social media accounts.',
                'Discuss local news or discussions related to cybercrime in Vanuatu.'
              ]
            },
            {
              name: 'Introducing Cyber Legislation',
              steps: [
                'Discuss the need for laws to address cybercrime.',
                'Define cyber legislation: laws enacted to prevent, investigate, and prosecute cybercrimes.',
                'Emphasize role in establishing legal frameworks for digital space.',
                'Mention Vanuatu\'s laws related to digital communication or online activities.',
                'Discuss Harmful Digital Communications Act and its provisions.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Markers/Pens',
            'Handout with definitions of cybercrime and cyber legislation',
            'Local news examples'
          ],
          assessment: [
            'Informal observation of student participation',
            'Quick Q&A to check understanding of definitions'
          ],
          homework: [
            'Find one news article (local or international) describing a cybercrime.'
          ],
          teacherNotes: [
            'Use simple language for complex concepts.',
            'Relate to students\' daily online experiences.',
            'Emphasize relevance to Vanuatu context.'
          ],
          differentiation: [
            '**For struggling learners:** Provide pre-written definitions with visual aids.',
            '**For advanced learners:** Ask them to compare Vanuatu\'s cyber legislation with other Pacific nations.'
          ]
        },
        {
          id: 'session2_4.2',
          number: 2,
          title: 'Cybercrime Concerns in ICT',
          duration: '45 minutes',
          structure: [
            { phase: 'Identifying Growing Concerns', time: '20 min' },
            { phase: 'Outlining Concerns', time: '20 min' },
            { phase: 'Local Relevance Discussion', time: '5 min' }
          ],
          introduction: [
            'Discuss increasing prevalence of cybercrime in ICT.',
            'Prompt students about current trends and news related to online security.',
            'Ask how these concerns affect Vanuatu\'s growing digital connectivity.'
          ],
          mainActivities: [
            {
              name: 'Identifying Growing Concerns',
              steps: [
                'Ransomware attacks: Encrypting data and demanding payment.',
                'Data breaches and privacy violations.',
                'Online scams and fraud (phishing, business email compromise).',
                'Cyberbullying and online harassment.',
                'Attacks on critical infrastructure.',
                'Discuss how scams affect communities with less familiarity with online transactions.'
              ]
            },
            {
              name: 'Outlining Concerns',
              steps: [
                'Provide structured outline of cybercrime concerns.',
                'Emphasize potential impact on individuals, organizations, and infrastructure.',
                'Discuss impact of data breaches on Vanuatu businesses (tourism, banks).',
                'Discuss consequences if government services are compromised.',
                'Identify which concerns are particularly relevant to Vanuatu.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'List of growing cybercrime concerns',
            'Vanuatu-specific case examples'
          ],
          assessment: [
            'Students verbally stating and outlining growing cybercrime concerns',
            'Brief quiz on identifying concerns and impacts'
          ],
          homework: [
            'Research one listed cybercrime concern and find an example of it in the Pacific region.'
          ],
          teacherNotes: [
            'Use local examples to maintain engagement.',
            'Discuss vulnerability of Vanuatu\'s developing digital infrastructure.',
            'Emphasize prevention and awareness.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on 2-3 major concerns with clear examples.',
            '**For advanced learners:** Research specific Vanuatu government cybersecurity initiatives.'
          ]
        },
        {
          id: 'session3_4.2',
          number: 3,
          title: 'Key Components of Cyber Legislation',
          duration: '45 minutes',
          structure: [
            { phase: 'Exploring Cyber Legislation', time: '25 min' },
            { phase: 'Identifying Components', time: '15 min' },
            { phase: 'Discussion & Summary', time: '5 min' }
          ],
          introduction: [
            'Introduce concept that cyber legislation includes various components.',
            'Guide students to research key components of known cyber legislation.',
            'Focus on Vanuatu\'s relevant laws or regional frameworks.'
          ],
          mainActivities: [
            {
              name: 'Exploring Cyber Legislation Components',
              steps: [
                'Research Vanuatu\'s Harmful Digital Communications Act or similar Pacific laws.',
                'Identify main components: definitions, penalties, investigation procedures.',
                'Discuss international cooperation mechanisms like Budapest Convention.',
                'Examine digital evidence provisions and prevention measures.',
                'Discuss importance of laws defining "unauthorized access" in Vanuatu context.'
              ]
            },
            {
              name: 'Identifying and Outlining Components',
              steps: [
                'Define specific cyber offenses (unauthorized access, data interference, fraud).',
                'Provisions for investigation and prosecution.',
                'International cooperation mechanisms.',
                'Penalties and sentencing guidelines.',
                'Measures for prevention and awareness.',
                'Create collective outline of main components.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Links to Harmful Digital Communications Act',
            'Budapest Convention reference materials',
            'Whiteboard/Projector'
          ],
          assessment: [
            'Students\' ability to articulate key components',
            'Collective outline of legislation components'
          ],
          homework: [
            'Choose one component and explain why it\'s important for combating cybercrime.'
          ],
          teacherNotes: [
            'Make legal concepts accessible to students.',
            'Use real-world scenarios to illustrate components.',
            'Emphasize relevance to daily digital activities.'
          ],
          differentiation: [
            '**For struggling learners:** Provide simplified outline with key terms explained.',
            '**For advanced learners:** Compare Vanuatu legislation with other jurisdictions.'
          ]
        },
        {
          id: 'session4_4.2',
          number: 4,
          title: 'Reported Cybercrimes',
          duration: '45 minutes',
          structure: [
            { phase: 'Researching Reported Cybercrimes', time: '30 min' },
            { phase: 'Sharing and Listing Findings', time: '15 min' }
          ],
          introduction: [
            'Guide students to research reported cybercrimes in Vanuatu and the Pacific.',
            'Encourage use of official reports, news articles, and government sources.',
            'Discuss the 2022 ransomware attack on Vanuatu government as a major example.'
          ],
          mainActivities: [
            {
              name: 'Researching Reported Cybercrimes',
              steps: [
                'Search Vanuatu Police Force website and news archives.',
                'Review Pacific Community (SPC) reports on regional cybercrime.',
                'Identify reported cases: online scams, social media fraud, data breaches.',
                'Document specific examples from Vanuatu and neighboring nations.',
                'Note common patterns and emerging threats in the region.'
              ]
            },
            {
              name: 'Sharing and Listing Findings',
              steps: [
                'Students share research findings with class.',
                'Collectively list major cybercrimes reported locally and regionally.',
                'Discuss image abuse, slander, cyberbullying cases in Vanuatu.',
                'Examine ATM fraud, forgery, cyber stalking, and online defamation.',
                'Analyze the 2022 government ransomware attack impact.',
                'Create comprehensive list on board.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'Vanuatu Police Force website links',
            'Pacific Community (SPC) reports',
            'Regional news outlets',
            'Whiteboard/Projector'
          ],
          assessment: [
            'Quality of research and examples provided',
            'Collectively generated list of reported cybercrimes'
          ],
          homework: [
            'Select one reported cybercrime and write a paragraph explaining its impact on Vanuatu.'
          ],
          teacherNotes: [
            'Ensure students verify information from credible sources.',
            'Discuss the challenge of underreporting in cybercrime.',
            'Emphasize the need for reporting suspicious activities.'
          ],
          differentiation: [
            '**For struggling learners:** Provide specific search terms and article templates.',
            '**For advanced learners:** Analyze trends and patterns across multiple cases.'
          ]
        },
        {
          id: 'session5_4.2',
          number: 5,
          title: 'Efforts to Combat Cybercrimes',
          duration: '45 minutes',
          structure: [
            { phase: 'Brainstorming Combating Efforts', time: '25 min' },
            { phase: 'Discussing Key Efforts', time: '20 min' }
          ],
          introduction: [
            'Initiate discussion on actions to fight cybercrime locally and globally.',
            'Ask what Vanuatu government or police might be doing.',
            'Discuss what individuals can do to protect themselves.'
          ],
          mainActivities: [
            {
              name: 'Local Efforts',
              steps: [
                'Strengthening national cyber legislation.',
                'Establishing specialized cybercrime units in law enforcement.',
                'Public awareness campaigns and training programs.',
                'Discuss importance of dedicated cybercrime unit in Vanuatu Police Force.',
                'Training for police officers on digital forensics.',
                'CERT Vanuatu\'s role in incident response and coordination.'
              ]
            },
            {
              name: 'Global Efforts',
              steps: [
                'International treaties: Budapest Convention on Cybercrime.',
                'INTERPOL cybercrime initiatives and coordination.',
                'Collaborations between nations for cross-border investigations.',
                'Development of international standards and best practices.',
                'Capacity building programs from organizations like SPC.',
                'Discuss how Vanuatu benefits from international training and technical assistance.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'INTERPOL cybercrime information',
            'UNODC cybercrime programs',
            'Pacific Community (SPC) resources',
            'Whiteboard/Projector'
          ],
          assessment: [
            'Students stating local and global efforts',
            'Class discussion on effectiveness and application'
          ],
          homework: [
            'Identify one local and one global effort and explain how they complement each other.'
          ],
          teacherNotes: [
            'Highlight Vanuatu\'s participation in regional initiatives.',
            'Discuss importance of international cooperation.',
            'Emphasize students can be advocates in their communities.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on 2-3 clear examples with visual aids.',
            '**For advanced learners:** Research specific regional cybersecurity frameworks.'
          ]
        },
        {
          id: 'session6_4.2',
          number: 6,
          title: 'Challenges for Pacific Island Nations',
          duration: '45 minutes',
          structure: [
            { phase: 'Analyzing Challenges', time: '30 min' },
            { phase: 'Assessing Efforts', time: '15 min' }
          ],
          introduction: [
            'Facilitate research-based discussion on challenges faced by Pacific Island Nations.',
            'Focus on issues specific to island geography and developing infrastructure.',
            'Discuss Vanuatu\'s experience with the 2022 ransomware attack.'
          ],
          mainActivities: [
            {
              name: 'Analyzing Challenges',
              steps: [
                'Limited technical expertise and resources in Vanuatu.',
                'Geographic isolation and connectivity issues across islands.',
                'Lack of comprehensive cyber legislation in some Pacific nations.',
                'Difficulty in cross-border investigations.',
                'How distance and island connectivity complicate cybercrime investigations.',
                'Awareness and education gaps within population.',
                'Resource constraints for law enforcement and judicial systems.'
              ]
            },
            {
              name: 'Assessing Efforts',
              steps: [
                'Regional collaborations through Pacific Community (SPC).',
                'Seeking international assistance and training.',
                'Developing national cybersecurity strategies.',
                'Evaluate effectiveness of current efforts.',
                'Identify areas for improvement.',
                'Discuss Vanuatu\'s participation in regional training programs.'
              ]
            }
          ],
          materials: [
            'Computers with internet access',
            'SPC reports on Pacific cybersecurity',
            'Articles on digital divide in Pacific Islands',
            'Whiteboard/Projector'
          ],
          assessment: [
            'Students identifying and explaining specific challenges',
            'Group discussion evaluating effectiveness of efforts'
          ],
          homework: [
            'Propose one practical solution Vanuatu could implement to address an identified challenge.'
          ],
          teacherNotes: [
            'Be sensitive to local resource limitations.',
            'Emphasize solutions-oriented thinking.',
            'Discuss career opportunities in addressing these challenges.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on 3 major challenges with clear explanations.',
            '**For advanced learners:** Develop comprehensive strategy addressing multiple challenges.'
          ]
        },
        {
          id: 'session7_4.2',
          number: 7,
          title: 'Preventative Plans to Minimize Cybercrime Effects',
          duration: '45 minutes',
          structure: [
            { phase: 'Brainstorming Preventative Measures', time: '25 min' },
            { phase: 'Discussing Important Factors', time: '20 min' }
          ],
          introduction: [
            'Shift focus to proactive steps individuals and organizations can take.',
            'Ask students for personal tips on staying safe online.',
            'Discuss importance of strong passwords for social media and email.'
          ],
          mainActivities: [
            {
              name: 'Individual Preventative Measures',
              steps: [
                'Awareness and Education: Understanding cyber threats and safe practices.',
                'Strong Security Practices: Passwords, MFA, software updates, antivirus.',
                'Regular phone app updates for security.',
                'Being cautious with personal information online.',
                'Backing up important data regularly.',
                'Using public awareness campaigns in local languages.'
              ]
            },
            {
              name: 'Organizational Preventative Measures',
              steps: [
                'Incident Response Planning: Having a plan for cyber incidents.',
                'Data Backup and Recovery: Regular backups to minimize data loss.',
                'Collaboration and Information Sharing: Sharing threat information.',
                'Legal Frameworks: Clear cyber legislation and enforcement.',
                'Employee training and security awareness programs.',
                'Network security measures and access controls.',
                'Reiterate how strong laws are a preventative measure for Vanuatu.'
              ]
            }
          ],
          materials: [
            'Whiteboard/Projector',
            'Handout summarizing preventative measures',
            'Visual aids for security best practices'
          ],
          assessment: [
            'Students identifying and explaining preventative factors',
            'Culminating class discussion on key takeaways'
          ],
          homework: [
            'Create a poster or short presentation on preventative measures for a specific Vanuatu audience (students, small business, elderly).'
          ],
          teacherNotes: [
            'Make preventative measures actionable and practical.',
            'Relate to daily activities students can implement immediately.',
            'Emphasize collective responsibility for cybersecurity.'
          ],
          differentiation: [
            '**For struggling learners:** Focus on 3-5 key measures with step-by-step guides.',
            '**For advanced learners:** Develop comprehensive cybersecurity policy for a Vanuatu organization.'
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 -m-6 p-6">
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <NotebookPen className="h-8 w-8 text-primary" />
              Detailed Lesson Plans
            </h1>
            <p className="text-muted-foreground mt-1">
              ICT Year 13 - Complete session-by-session teaching plans with activities and resources
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={() => navigate('/dashboard/curriculum?tab=units')}
            >
              <ExternalLink className="h-4 w-4" />
              View Curriculum
            </Button>
          </div>
        </div>

        {/* Strand Selector */}
        <Card>
          <CardContent className="pt-6">
            <Tabs value={selectedStrand} onValueChange={setSelectedStrand}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="strand1">Strand 1: PC Systems</TabsTrigger>
                <TabsTrigger value="strand2">Strand 2: Web Dev</TabsTrigger>
                <TabsTrigger value="strand3">Strand 3: Programming</TabsTrigger>
                <TabsTrigger value="strand4">Strand 4: Cybersecurity</TabsTrigger>
                <TabsTrigger value="strand5" disabled>Strand 5: ICT Issues</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        {/* Strand 1 Content */}
        {selectedStrand === 'strand1' && (
          <div className="space-y-4">
            {strand1Lessons.map((lesson) => (
              <Card key={lesson.id} className="overflow-hidden shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Book className="h-5 w-5 text-blue-700" />
                        {lesson.unit}: {lesson.title}
                      </CardTitle>
                      <CardDescription className="mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {lesson.weeks}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {lesson.duration}
                        </span>
                        {lesson.linkedToScheme && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Linked to Scheme
                          </Badge>
                        )}
                        {lesson.linkedToBlueprint && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <FileText className="h-3 w-3 mr-1" />
                            Has Blueprint
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {/* Sessions */}
                  {lesson.sessions.map((session) => (
                    <Card key={session.id} className="border-2 border-blue-200">
                      <CardHeader 
                        className="cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => toggleSession(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-blue-600">Session {session.number}</Badge>
                            <div>
                              <CardTitle className="text-base">{session.title}</CardTitle>
                              <CardDescription className="text-xs mt-1">
                                <Clock className="h-3 w-3 inline mr-1" />
                                {session.duration}
                              </CardDescription>
                            </div>
                          </div>
                          {expandedSession === session.id ? 
                            <ChevronDown className="h-5 w-5" /> : 
                            <ChevronRight className="h-5 w-5" />
                          }
                        </div>
                      </CardHeader>

                      {expandedSession === session.id && (
                        <CardContent className="pt-4 space-y-6">
                          {/* Lesson Structure */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Activity className="h-4 w-4 text-purple-600" />
                              Lesson Structure & Timing
                            </h4>
                            <div className="grid md:grid-cols-2 gap-3">
                              {session.structure.map((phase, idx) => (
                                <div key={idx} className="border rounded-lg p-3 bg-purple-50/30">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">{phase.phase}</span>
                                    <Badge variant="outline" className="text-xs">{phase.time}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Introduction */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Lightbulb className="h-4 w-4 text-yellow-600" />
                              Introduction Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-2 text-muted-foreground ml-2">
                              {session.introduction.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Main Activities */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-green-600" />
                              Main Activities
                            </h4>
                            <div className="space-y-4">
                              {session.mainActivities.map((activity, idx) => (
                                <Card key={idx} className="border-l-4 border-l-green-500">
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-sm">{activity.name}</CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <ol className="list-decimal list-inside text-sm space-y-1 text-muted-foreground">
                                      {activity.steps.map((step, stepIdx) => (
                                        <li key={stepIdx} className="ml-2">{step}</li>
                                      ))}
                                    </ol>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Materials */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <FileText className="h-4 w-4 text-orange-600" />
                              Materials & Resources
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {session.materials.map((material, idx) => (
                                <Badge key={idx} variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                                  {material}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Assessment */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <CheckSquare className="h-4 w-4 text-indigo-600" />
                              Assessment Methods
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.assessment.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Homework */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <GraduationCap className="h-4 w-4 text-teal-600" />
                              Homework / Follow-up Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.homework.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Teacher Notes */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-amber-900">
                              <Target className="h-4 w-4" />
                              Teacher Notes & Tips
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-amber-800 ml-2">
                              {session.teacherNotes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Differentiation */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-blue-900">
                              <Users className="h-4 w-4" />
                              Differentiation Strategies
                            </h4>
                            <div className="space-y-2 text-sm text-blue-800">
                              {session.differentiation.map((item, idx) => (
                                <p key={idx}>{item}</p>
                              ))}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4">
                            <Button size="sm" className="gap-2">
                              <Download className="h-3 w-3" />
                              Download Session Plan
                            </Button>
                            <Button size="sm" variant="outline" className="gap-2">
                              <FileText className="h-3 w-3" />
                              Print Handouts
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </CardContent>
              </Card>
            ))}

            {/* Coming Soon Card */}
            <Card className="border-2 border-dashed border-blue-300">
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">More Lesson Plans Coming Soon</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Detailed lesson plans for Units 1.4-1.7 are being prepared.
                </p>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  In Development
                </Badge>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Strand 2 Content */}
        {selectedStrand === 'strand2' && (
          <div className="space-y-4">
            {strand2Lessons.map((lesson) => (
              <Card key={lesson.id} className="overflow-hidden shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-blue-600">{lesson.unit}</Badge>
                        <CardTitle className="text-xl">{lesson.title}</CardTitle>
                      </div>
                      <CardDescription className="flex flex-wrap items-center gap-4 mt-2">
                        <span className="flex items-center gap-1 text-sm">
                          <Calendar className="h-4 w-4" />
                          {lesson.weeks}
                        </span>
                        <span className="flex items-center gap-1 text-sm">
                          <Clock className="h-4 w-4" />
                          {lesson.duration}
                        </span>
                        {lesson.linkedToScheme && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Linked to Scheme
                          </Badge>
                        )}
                        {lesson.linkedToBlueprint && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            <CheckSquare className="h-3 w-3 mr-1" />
                            Has Blueprint
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  {lesson.sessions.map((session) => (
                    <Card 
                      key={session.id}
                      className={`transition-all duration-200 hover:shadow-md ${
                        expandedSession === session.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                    >
                      <CardHeader 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleSession(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-blue-600">Session {session.number}</Badge>
                            <div>
                              <CardTitle className="text-base">{session.title}</CardTitle>
                              <CardDescription className="flex items-center gap-1 text-xs mt-1">
                                <Clock className="h-3 w-3" />
                                {session.duration}
                              </CardDescription>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            {expandedSession === session.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardHeader>
                      
                      {expandedSession === session.id && (
                        <CardContent className="space-y-6 animate-in slide-in-from-top-2 duration-300">
                          {/* Lesson Structure */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Activity className="h-4 w-4 text-blue-600" />
                              Lesson Structure
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {session.structure.map((phase, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                  <span className="text-sm font-medium">{phase.phase}</span>
                                  <Badge variant="outline">{phase.time}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Introduction */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Lightbulb className="h-4 w-4 text-yellow-600" />
                              Introduction Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-2 text-muted-foreground ml-2">
                              {session.introduction.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Main Activities */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-green-600" />
                              Main Activities
                            </h4>
                            <div className="space-y-4">
                              {session.mainActivities.map((activity, idx) => (
                                <Card key={idx} className="border-l-4 border-l-green-500">
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-sm">{activity.name}</CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <ol className="list-decimal list-inside text-sm space-y-1 text-muted-foreground">
                                      {activity.steps.map((step, stepIdx) => (
                                        <li key={stepIdx} className="ml-2">{step}</li>
                                      ))}
                                    </ol>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Materials */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <FileText className="h-4 w-4 text-orange-600" />
                              Materials & Resources
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {session.materials.map((material, idx) => (
                                <Badge key={idx} variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                                  {material}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Assessment */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <CheckSquare className="h-4 w-4 text-indigo-600" />
                              Assessment Methods
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.assessment.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Homework */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <GraduationCap className="h-4 w-4 text-teal-600" />
                              Homework / Follow-up Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.homework.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Teacher Notes */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-amber-900">
                              <Target className="h-4 w-4" />
                              Teacher Notes & Tips
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-amber-800 ml-2">
                              {session.teacherNotes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Differentiation */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-blue-900">
                              <Users className="h-4 w-4" />
                              Differentiation Strategies
                            </h4>
                            <div className="space-y-2 text-sm text-blue-800">
                              {session.differentiation.map((item, idx) => (
                                <p key={idx}>{item}</p>
                              ))}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4">
                            <Button size="sm" className="gap-2">
                              <Download className="h-3 w-3" />
                              Download Session Plan
                            </Button>
                            <Button size="sm" variant="outline" className="gap-2">
                              <FileText className="h-3 w-3" />
                              Print Handouts
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </CardContent>
              </Card>
            ))}

            {/* Coming Soon Card */}
            <Card className="border-2 border-dashed border-blue-300">
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">More Lesson Plans Coming Soon</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Detailed lesson plans for Units 2.3-2.7 are being prepared.
                </p>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  In Development
                </Badge>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Strand 3 Content */}
        {selectedStrand === 'strand3' && (
          <div className="space-y-4">
            {strand3Lessons.map((lesson) => (
              <Card key={lesson.id} className="overflow-hidden shadow-lg">
                <CardHeader className="bg-gradient-to-r from-purple-100 to-pink-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-purple-600">{lesson.unit}</Badge>
                        <CardTitle className="text-xl">{lesson.title}</CardTitle>
                      </div>
                      <CardDescription className="flex flex-wrap items-center gap-4 mt-2">
                        <span className="flex items-center gap-1 text-sm">
                          <Calendar className="h-4 w-4" />
                          {lesson.weeks}
                        </span>
                        <span className="flex items-center gap-1 text-sm">
                          <Clock className="h-4 w-4" />
                          {lesson.duration}
                        </span>
                        {lesson.linkedToScheme && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Linked to Scheme
                          </Badge>
                        )}
                        {lesson.linkedToBlueprint && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            <CheckSquare className="h-3 w-3 mr-1" />
                            Has Blueprint
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-3">
                  {lesson.sessions.map((session) => (
                    <Card 
                      key={session.id}
                      className={`transition-all duration-200 hover:shadow-md ${
                        expandedSession === session.id ? 'ring-2 ring-purple-500' : ''
                      }`}
                    >
                      <CardHeader 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleSession(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-purple-600">Session {session.number}</Badge>
                            <div>
                              <CardTitle className="text-base">{session.title}</CardTitle>
                              <CardDescription className="flex items-center gap-1 text-xs mt-1">
                                <Clock className="h-3 w-3" />
                                {session.duration}
                              </CardDescription>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm">
                            {expandedSession === session.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardHeader>
                      
                      {expandedSession === session.id && (
                        <CardContent className="space-y-6 animate-in slide-in-from-top-2 duration-300">
                          {/* Lesson Structure */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Activity className="h-4 w-4 text-purple-600" />
                              Lesson Structure
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {session.structure.map((phase, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                  <span className="text-sm font-medium">{phase.phase}</span>
                                  <Badge variant="outline">{phase.time}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Introduction */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Lightbulb className="h-4 w-4 text-yellow-600" />
                              Introduction Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-2 text-muted-foreground ml-2">
                              {session.introduction.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Main Activities */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-green-600" />
                              Main Activities
                            </h4>
                            <div className="space-y-4">
                              {session.mainActivities.map((activity, idx) => (
                                <Card key={idx} className="border-l-4 border-l-green-500">
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-sm">{activity.name}</CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    <ol className="list-decimal list-inside text-sm space-y-1 text-muted-foreground">
                                      {activity.steps.map((step, stepIdx) => (
                                        <li key={stepIdx} className="ml-2">{step}</li>
                                      ))}
                                    </ol>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Materials */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <FileText className="h-4 w-4 text-orange-600" />
                              Materials & Resources
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {session.materials.map((material, idx) => (
                                <Badge key={idx} variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                                  {material}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Assessment */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <CheckSquare className="h-4 w-4 text-indigo-600" />
                              Assessment Methods
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.assessment.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Homework */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <GraduationCap className="h-4 w-4 text-teal-600" />
                              Homework / Follow-up Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground ml-2">
                              {session.homework.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <Separator />

                          {/* Teacher Notes */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-amber-900">
                              <Target className="h-4 w-4" />
                              Teacher Notes & Tips
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-amber-800 ml-2">
                              {session.teacherNotes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Differentiation */}
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-purple-900">
                              <Users className="h-4 w-4" />
                              Differentiation Strategies
                            </h4>
                            <div className="space-y-2 text-sm text-purple-800">
                              {session.differentiation.map((item, idx) => (
                                <p key={idx}>{item}</p>
                              ))}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4">
                            <Button size="sm" className="gap-2">
                              <Download className="h-3 w-3" />
                              Download Session Plan
                            </Button>
                            <Button size="sm" variant="outline" className="gap-2">
                              <FileText className="h-3 w-3" />
                              Print Handouts
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </CardContent>
              </Card>
            ))}

            {/* Coming Soon Card */}
            <Card className="border-2 border-dashed border-purple-300">
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">More Lesson Plans Coming Soon</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Additional units for Strand 3 Programming are being prepared.
                </p>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  In Development
                </Badge>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Strand 4 Content */}
        {selectedStrand === 'strand4' && (
          <div className="space-y-4">
            {strand4Lessons.map((lesson) => (
              <Card key={lesson.id} className="overflow-hidden shadow-lg">
                <CardHeader className="bg-gradient-to-r from-red-100 to-orange-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Book className="h-5 w-5 text-red-700" />
                        {lesson.unit}: {lesson.title}
                      </CardTitle>
                      <CardDescription className="mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {lesson.weeks}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {lesson.duration}
                        </span>
                        {lesson.linkedToScheme && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <LinkIcon className="h-3 w-3 mr-1" />
                            Linked to Scheme
                          </Badge>
                        )}
                        {lesson.linkedToBlueprint && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <FileText className="h-3 w-3 mr-1" />
                            Has Blueprint
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  {/* Sessions */}
                  {lesson.sessions.map((session) => (
                    <Card key={session.id} className="border-2 border-red-200">
                      <CardHeader 
                        className="cursor-pointer hover:bg-red-50 transition-colors"
                        onClick={() => toggleSession(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-red-600">Session {session.number}</Badge>
                            <div>
                              <CardTitle className="text-base">{session.title}</CardTitle>
                              <CardDescription className="text-xs mt-1">
                                <Clock className="h-3 w-3 inline mr-1" />
                                {session.duration}
                              </CardDescription>
                            </div>
                          </div>
                          {expandedSession === session.id ? 
                            <ChevronDown className="h-5 w-5" /> : 
                            <ChevronRight className="h-5 w-5" />
                          }
                        </div>
                      </CardHeader>

                      {expandedSession === session.id && (
                        <CardContent className="pt-4 space-y-6">
                          {/* Lesson Structure & Timing */}
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-purple-900">
                              <Activity className="h-4 w-4" />
                              Lesson Structure & Timing
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {session.structure.map((phase, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-purple-200">
                                  <span className="text-sm font-medium">{phase.phase}</span>
                                  <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                                    {phase.time}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Introduction Activities */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-yellow-900">
                              <Lightbulb className="h-4 w-4" />
                              Introduction Activities
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-yellow-800 ml-2">
                              {session.introduction.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Main Activities */}
                          <div>
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-green-700" />
                              Main Activities
                            </h4>
                            <div className="space-y-3">
                              {session.mainActivities.map((activity, idx) => (
                                <Card key={idx} className="border-l-4 border-l-green-500">
                                  <CardContent className="pt-4">
                                    <h5 className="font-semibold text-sm mb-2 text-green-900">{activity.name}</h5>
                                    <ul className="list-decimal list-inside text-sm space-y-1 text-muted-foreground ml-2">
                                      {activity.steps.map((step, stepIdx) => (
                                        <li key={stepIdx}>{step}</li>
                                      ))}
                                    </ul>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>

                          {/* Materials & Resources */}
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-orange-900">
                              <FileText className="h-4 w-4" />
                              Materials & Resources
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {session.materials.map((material, idx) => (
                                <Badge key={idx} variant="outline" className="bg-white text-orange-800 border-orange-300">
                                  {material}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          {/* Assessment Methods */}
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-indigo-900">
                              <CheckSquare className="h-4 w-4" />
                              Assessment Methods
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-indigo-800 ml-2">
                              {session.assessment.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Homework/Follow-up */}
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-teal-900">
                              <GraduationCap className="h-4 w-4" />
                              Homework / Follow-up
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-teal-800 ml-2">
                              {session.homework.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Teacher Notes */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-amber-900">
                              <Target className="h-4 w-4" />
                              Teacher Notes & Tips
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1 text-amber-800 ml-2">
                              {session.teacherNotes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          </div>

                          {/* Differentiation */}
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-purple-900">
                              <Users className="h-4 w-4" />
                              Differentiation Strategies
                            </h4>
                            <div className="space-y-2 text-sm text-purple-800">
                              {session.differentiation.map((item, idx) => (
                                <p key={idx}>{item}</p>
                              ))}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4">
                            <Button size="sm" className="gap-2">
                              <Download className="h-3 w-3" />
                              Download Session Plan
                            </Button>
                            <Button size="sm" variant="outline" className="gap-2">
                              <FileText className="h-3 w-3" />
                              Print Handouts
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </CardContent>
              </Card>
            ))}

            {/* Coming Soon Card */}
            <Card className="border-2 border-dashed border-red-300">
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 text-red-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">More Lesson Plans Coming Soon</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Additional units for Strand 4 Cybersecurity are being prepared.
                </p>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  In Development
                </Badge>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
